/**
 * Wiederverwendbare UI-Bausteine — exakt im Stitch-Design ("Premium Hospitality",
 * Material-3-Token). Quelle: maestro_dashboard/code.html u. a. Stitch-Exporte.
 */
import {
  createContext,
  useCallback,
  useId,
  forwardRef,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ChangeEvent,
  type FocusEvent,
  type FormEvent,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, useInRouterContext, type LinkProps } from "react-router-dom";
import { useHerkunft } from "../lib/rueckweg";
import { useOverlayOpenSignal } from "../hooks/useOverlayOpen";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadixSelect from "@radix-ui/react-select";
import * as Tabs from "@radix-ui/react-tabs";
import * as Toast from "@radix-ui/react-toast";
import { GRUEN_PILL } from "../lib/zahlungsstand";
import { PILL_CLASSES, mailFehlerKurzfassung, type StatusMeta } from "./format";

/**
 * UX-Paket 26.08.2026, Punkt 1: gemeinsamer Baustein für `Drawer`/`Modal` — ein Klick auf den
 * Hintergrund oder Escape schloss bisher IMMER sofort, auch mit ungespeichertem Formularinhalt
 * (kein Dirty-Check). Der Verlust war still: kein Hinweis, kein Undo. Dieser Hook macht daraus
 * einen bewussten Schritt — `dirty` wird per `onChangeCapture` am Container gesetzt (fängt jede
 * Tastatureingabe/jeden Feldwechsel, egal ob controlled/uncontrolled), bei `open` zurückgesetzt.
 * Der explizite "Abbrechen"-Button jedes Formulars bleibt bewusst UNGEGUARDET — ein Klick darauf
 * ist eine absichtliche Entscheidung, kein Versehen wie Backdrop-Klick/Escape.
 *
 * Zweiter Betriebsmodus `autosave` (Bughunt 01.09.2026, Antoine am "Eckdaten der
 * Veranstaltung"-Fenster: „Er gibt diese Warnung aus, speichert aber dennoch?"): Der Guard ging
 * bisher stillschweigend davon aus, dass JEDES Formular hinter `Drawer`/`Modal` eine echte
 * Sammel-„Speichern"-Aktion hat, die die Tastatureingabe erst festschreibt — bei einem
 * Autosave-Formular (jedes Feld schreibt einzeln per `onBlur`/`onChange` sofort per PATCH, kein
 * Sammel-Knopf) gibt es diesen ungeschriebenen Zwischenstand nie: `dirty` wurde trotzdem bei
 * jeder Tastatureingabe gesetzt, weil `onChangeCapture` nicht wissen kann, ob das Feld dahinter
 * autosave-t. Die Warnung log dort also grundsaetzlich, ganz gleich was im Feld stand. Mit
 * `autosave: true` wird `onChangeCapture` zum No-Op (nichts markiert `dirty`, es bleibt immer
 * `false`) und `requestClose` schliesst direkt ohne `window.confirm` — Vorgabe bleibt `false`
 * (unveraendertes Verhalten) fuer jeden bestehenden Aufrufer mit echtem Speichern-Knopf, z. B.
 * "Veranstaltung bearbeiten" in event-detail.tsx.
 */
function useCloseGuard(
  open: boolean,
  onClose: () => void,
  autosave = false,
): { requestClose: () => void; onChangeCapture: () => void; dialog: ReactNode } {
  const dirty = useRef(false);
  // Design-Durchsicht 26.09.2026, Entscheidung 12: die Rückfrage ist der MAESTRO-Bestätigungsdialog
  // statt `window.confirm` — Wortlaut und Verhalten unverändert.
  const { bestaetigen, dialog } = useBestaetigung();
  useEffect(() => {
    if (open) dirty.current = false;
  }, [open]);
  async function requestClose() {
    if (
      !autosave &&
      dirty.current &&
      !(await bestaetigen({ titel: "Ungespeicherte Änderungen verwerfen?", bestaetigen: "Verwerfen", gefahr: true }))
    )
      return;
    onClose();
  }
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") void requestClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  return {
    requestClose: () => void requestClose(),
    onChangeCapture: autosave ? () => {} : () => (dirty.current = true),
    dialog,
  };
}

/** Was eine Rückfrage zeigt. `titel` ist die Frage selbst („Zahlung wirklich löschen?“), `text` die
 *  Folgen, `bestaetigen` das Verb auf dem Knopf. `gefahr` färbt den Knopf ziegelrot (Löschen,
 *  Stornieren, Geld bewegen). `abbrechen: false` macht daraus einen reinen Hinweis mit nur einem
 *  Knopf (Ersatz für `window.alert`). */
export type BestaetigungsFrage = {
  titel: string;
  text?: ReactNode;
  bestaetigen?: string;
  abbrechen?: string | false;
  gefahr?: boolean;
};

/**
 * Design-Durchsicht 26.09.2026, Entscheidung 12: Ersatz für `window.confirm`. Aufruf wie bisher
 * `if (!(await bestaetigen({...}))) return;`, dazu `{dialog}` einmal irgendwo in den eigenen JSX
 * setzen. Der Dialog hängt per Portal an `document.body` (der Copilot liegt in einem verschobenen
 * Container, dort würde `position: fixed` sonst am falschen Bezug hängen) und liegt über jedem
 * offenen Drawer/Modal. Escape beantwortet nur die Rückfrage mit „Nein“ und schließt nicht auch
 * noch das Fenster darunter.
 */
export function useBestaetigung(): { bestaetigen: (frage: BestaetigungsFrage) => Promise<boolean>; dialog: ReactNode } {
  const [offen, setOffen] = useState<(BestaetigungsFrage & { antwort: (ok: boolean) => void }) | null>(null);
  const bestaetigen = useCallback(
    (frage: BestaetigungsFrage) => new Promise<boolean>((antwort) => setOffen({ ...frage, antwort })),
    [],
  );
  function antworten(ok: boolean) {
    offen?.antwort(ok);
    setOffen(null);
  }
  useEffect(() => {
    if (!offen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      antworten(false);
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [offen]);
  const dialog = offen
    ? createPortal(
        <Modal
          open
          oben
          onClose={() => antworten(false)}
          width="440px"
          title={offen.titel}
          footer={
            <>
              {offen.abbrechen !== false && (
                <Button type="button" variant="secondary" onClick={() => antworten(false)}>
                  {offen.abbrechen ?? "Abbrechen"}
                </Button>
              )}
              <Button type="button" variant={offen.gefahr ? "danger" : "primary"} autoFocus onClick={() => antworten(true)}>
                {offen.bestaetigen ?? "Bestätigen"}
              </Button>
            </>
          }
        >
          {offen.text != null && (
            <div className="whitespace-pre-line font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              {offen.text}
            </div>
          )}
        </Modal>,
        document.body,
      )
    : null;
  return { bestaetigen, dialog };
}

export function Icon({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

/** Status-Pill nach Claude-Design-Entwurf: Label + fuehrender Farbpunkt (erbt die Ton-Farbe via currentColor).
 *  `testId` ist optional (E2E, apps/e2e/README.md § "Selektoren"): dasselbe Label steht auf
 *  event-detail.tsx auch im Status-Stepper (jeder Schritt zeigt seinen Namen permanent) - reines
 *  Text-Matching auf den Kopf-Badge waere dort mehrdeutig.
 *  `icon` (optional, Bughunt 28.08.2026 — Mailversand-Pillen): ersetzt den Punkt durch ein
 *  Material-Icon, wenn die Unterscheidung nicht allein an der Farbe haengen soll (z. B. Erfolg
 *  vs. Fehlschlag beim Mailversand, wo "Gesendet" und "Fehlgeschlagen" vorher farblich kaum
 *  auseinanderzuhalten waren). Ohne `icon` unveraendertes Verhalten — alle bisherigen Aufrufer
 *  (Vorgangs-/Zahlungs-/Anfrage-Status usw.) bleiben unangetastet. */
export function StatusPill({ meta, testId, icon }: { meta: StatusMeta | undefined; testId?: string; icon?: string }) {
  if (!meta) return null;
  // Ruhige Hinweise (13.10.2026): die Bedeutung traegt das Symbol, nicht die Farbe — darum
  // faellt die Pille auf `meta.icon` der Status-Tabelle zurueck.
  icon = icon ?? meta.icon;
  return (
    <span
      data-testid={testId}
      title={meta.title}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.gruen ? GRUEN_PILL : PILL_CLASSES[meta.tone]}`}
    >
      {icon ? <Icon name={icon} className="text-[13px]" /> : <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
      {meta.label}
    </span>
  );
}

/**
 * Mailversand-Fehlertext (Bughunt 28.08.2026): kurze verstaendliche Ursache
 * (`mailFehlerKurzfassung`) + der Original-Rohtext als aufklappbare "Technische Details" fuer
 * Support/Fehlersuche — geht NICHT verloren, ist nur nicht mehr die Hauptdarstellung. Geteilt
 * zwischen `event-detail.tsx` (Aktivitaets-Feed) und `mail-protokoll.tsx` — beide zeigen
 * denselben Rohtext aus `emails.error`. */
export function MailFehlerHinweis({ rawError }: { rawError: string }) {
  return (
    <div className="mt-1.5 space-y-1">
      <p className="flex items-start gap-1.5 font-body-sm text-body-sm text-error">
        <Icon name="error" className="mt-0.5 shrink-0 text-[15px]" />
        <span>{mailFehlerKurzfassung(rawError)}</span>
      </p>
      <details>
        <summary className="cursor-pointer select-none font-label-md text-[11px] font-medium text-secondary hover:text-on-surface-variant dark:text-secondary-fixed-dim">
          Technische Details
        </summary>
        <p className="mt-1 whitespace-pre-wrap break-all rounded-md bg-surface-container-low px-2.5 py-2 font-mono text-[11px] leading-relaxed text-secondary dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
          {rawError}
        </p>
      </details>
    </div>
  );
}

/**
 * KPI-Kachel nach dem Design-Export: weiße Karte, warme Linie, weicher Schatten;
 * Icon-Chip (akzent-getönt) oben links, optionales Delta-Badge oben rechts, große
 * Zahl (26px) darüber das Label. Einheitlich für alle Kacheln (kein gefülltes Terracotta mehr).
 */
export function KpiCard({
  label,
  value,
  icon,
  delta,
  iconTone = "primary",
  to,
  onClick,
}: {
  label: string;
  value: ReactNode;
  icon?: string;
  delta?: { text: string; tone?: "up" | "warn" | "down" | "neutral" };
  /** H9: semantische Icon-Farbe statt immer Accent (z. B. Zahlungen-KPIs gruen/amber/blau).
   *  `danger` (Admin-Bereich V2 P1, Dashboard-Startseite): dieselben Farbwerte wie
   *  `PILL_CLASSES.danger`/`deltaTone.down` oben — kein neuer Farbton, nur derselbe bereits
   *  etablierte "wirklich kritisch"-Ton (rot) auch als Icon-Chip verfuegbar, fuer Kacheln wie
   *  "aktive Impersonationen", bei denen `warn` (amber, "braucht Aufmerksamkeit") zu schwach waere. */
  iconTone?: "primary" | "success" | "warn" | "info" | "danger";
  /** CX-Audit 2026-07-17: optionales Klickziel — eine KPI-Kachel ist eine Frage
   *  („Wie viele offene Anfragen?"), der Klick fuehrt zur Antwort-Liste statt in
   *  eine Sackgasse. Ohne `to` bleibt die Kachel reine Anzeige. */
  to?: string;
  /** Admin-Bereich V2 P1 (Dashboard-Startseite): Alternative zu `to` fuer Kontexte, die OHNE
   *  react-router-`<Route>`-Registrierung navigieren (`hooks/useAdminNavigation.ts` Kopfkommentar
   *  — ein `Link`-Klick wuerde die URL per `history.pushState` aendern, aber KEIN `popstate`
   *  ausloesen, auf das dieser Mini-Router lauscht; die sichtbare Seite bliebe hinter der URL
   *  zurueck). Rendert dieselbe klickbare Kachel als `<button>` statt `<Link>`. Wird `to` UND
   *  `onClick` gesetzt, gewinnt `to` (unveraendertes Verhalten fuer alle Bestandsaufrufer). */
  onClick?: () => void;
}) {
  // Ruhige Hinweise (13.10.2026): Delta-Chips und Icon-Kacheln sind grau — nur `down`/`danger`
  // (echter Fehler, z. B. aktive Impersonationen) bleibt rot. Die Bedeutung traegt das Symbol.
  const ruhig = "text-secondary bg-[#F0EAE0] dark:bg-white/[0.08] dark:text-[#b8ae9e]";
  const rot = "text-[#B23B32] bg-[#F8E7E3] dark:bg-[#B23B32]/22 dark:text-[#e39b92]";
  const deltaTone = delta?.tone === "down" ? rot : ruhig;
  const iconToneCls = iconTone === "danger" ? rot : ruhig;
  const baseCls =
    "rounded-[14px] border border-surface-variant bg-surface-container-lowest p-4 shadow-[0_8px_18px_-10px_rgba(45,38,26,0.14)] dark:border-white/10 dark:bg-night-card dark:shadow-[0_8px_18px_-10px_rgba(0,0,0,0.5)]";
  const body = (
    <>
      <div className="flex items-center justify-between">
        {icon ? (
          <div className={`grid h-[30px] w-[30px] place-items-center rounded-[9px] ${iconToneCls}`}>
            <Icon name={icon} className="text-[18px]" />
          </div>
        ) : (
          <span />
        )}
        {delta && (
          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${deltaTone}`}>{delta.text}</span>
        )}
      </div>
      <div className="mt-2.5">
        <div className="text-[26px] font-bold leading-none tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">
          {value}
        </div>
        <div className="mt-1.5 text-[11.5px] font-medium text-secondary dark:text-secondary-fixed-dim">{label}</div>
      </div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className={`${baseCls} block transition-shadow hover:shadow-[0_10px_22px_-10px_rgba(45,38,26,0.28)] dark:hover:shadow-[0_10px_22px_-10px_rgba(0,0,0,0.7)]`}
      >
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseCls} block w-full text-left transition-shadow hover:shadow-[0_10px_22px_-10px_rgba(45,38,26,0.28)] dark:hover:shadow-[0_10px_22px_-10px_rgba(0,0,0,0.7)]`}
      >
        {body}
      </button>
    );
  }
  return <div className={baseCls}>{body}</div>;
}

/** Rechts einfahrender Detail-Drawer mit Backdrop-Blur (Stitch: "Drawers slide in from the right").
 *  `width` uebersteuert die Standardbreite (max-w-lg = 512px), `subtitle` blendet eine Meta-Zeile
 *  unter dem Titel ein, `headerAccessory` platziert ein Element (z. B. Status-Pill) inline im Kopf.
 *
 *  DIE RECHTE KANTE — Historie: Von Befund 31.07.2026 bis 01.09.2026 liess die Huelle ab lg per
 *  CSS-Variable rechts Platz frei, damit der Drawer LINKS neben der angedockten Chat-Spalte sass
 *  statt sie zu ueberlagern (Antoine hatte per Chat eine Anfrage geoeffnet, die Schublade legte
 *  sich ueber die Spalte mit den Schrittzeilen, die gerade zeigten, was der Copilot tat). Der Wert
 *  kam aus index.css, abgeleitet von einem Datenattribut auf dem Wurzelelement, das AppShell
 *  setzte, sobald der Copilot ueberhaupt offen war — unabhaengig davon, ob er wirklich als volle
 *  Spalte sichtbar war.
 *
 *  Mit dem Schweb-Fenster-Redesign (28.08.2026) ist der Copilot keine feste angedockte Spalte
 *  mehr, sondern ein frei schwebendes, vom Nutzer verschiebbares und einklappbares Fenster
 *  (CopilotPanel.tsx, useFloatingWindow) — Topbar/Main reservieren ihm seitdem keinen Platz mehr.
 *  Nur dieser Drawer-Ausweich-Mechanismus wurde dabei vergessen und reservierte weiter blind
 *  Platz, auch wenn der Copilot nur als kleine eingeklappte Blase in der Ecke sichtbar war, gar
 *  nicht an der rechten Kante angedockt oder vom Nutzer weggezogen wurde — genau das erzeugte die
 *  sichtbare Luecke am rechten Rand. Auf Antoines expliziten Wunsch (01.09.2026: "Solche Fenster
 *  muessen immer rechts buendig sein") wurde die Aussparung deshalb noch am selben Tag
 *  vollstaendig entfernt: der Drawer war ab da unabhaengig vom Copilot-Zustand rechts buendig —
 *  ein frei verschiebbares Fenster braucht keine dauerhafte Platzreservierung mehr.
 *
 *  Nachbesserung 01.09.2026 (Fund eines unabhaengigen Review-Agenten direkt im Anschluss an obigen
 *  Rueckbau): uebersehen wurde dabei, dass der Copilot WEITERHIN einen echten "Fest verankern"-
 *  Modus hat (Pin-Knopf in CopilotPanel.tsx, `floatWin.mode === "pinned"`), der laut AppShell.tsx
 *  eine ECHTE, den Hauptinhalt verdraengende 380px-Spalte rechts ist (`PINNED_W` in
 *  useFloatingWindow.ts; Topbar/Main lassen dafuer bereits per `lg:mr-[380px]` Platz, siehe dort
 *  `pinnedOpen`). Ohne jede Aussparung legte sich ein offener Drawer in genau diesem selteneren,
 *  aber ueber den Pin-Knopf weiterhin erreichbaren Fall komplett UEBER die angepinnte Spalte —
 *  derselbe Fehler wie am 31.07.2026, nur diesmal im Pinned- statt im alten Standard-Andock-Fall.
 *  Antoines Wunsch ("immer rechts buendig") bezog sich erkennbar auf den Normalfall (Screenshot
 *  zeigte den Copiloten als kleine eingeklappte Blase, NICHT angepinnt) und widerspricht nicht
 *  einer Aussparung genau dann, wenn ER SELBST die Spalte bewusst angepinnt hat. Die CSS-Variable
 *  `--copilot-pinned-w` (gesetzt in AppShell.tsx, exakt dort wo `pinnedOpen` berechnet wird) ist
 *  deshalb 0px in jedem anderen Zustand (frei schwebend, eingeklappt, geschlossen) und nur in
 *  diesem einen Fall 380px — der Drawer bleibt in jedem anderen Zustand weiterhin immer rechts
 *  buendig, wie gewuenscht. Fallback `0px` greift zusaetzlich, falls die Variable aus irgendeinem
 *  Grund fehlt (z. B. AppShell nicht geladen — dasselbe Sicherheitsnetz wie beim alten
 *  `--copilot-offset`-Mechanismus).
 *  Unter lg unveraendert (voller Overlay-Drawer, rechts buendig). */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  breite = "mittel",
  subtitle,
  headerAccessory,
  autosave = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Design-Durchsicht 26.09.2026, Entscheidung 4: nur noch drei feste Breiten statt fünf freier
   *  Pixelwerte (480/512/540/640/760), damit Drawer beim Wechsel nicht springen. Auf dem Handy
   *  immer volle Breite. */
  breite?: keyof typeof DRAWER_BREITE;
  subtitle?: ReactNode;
  headerAccessory?: ReactNode;
  /** Bughunt 01.09.2026 (s. `useCloseGuard` oben): `true` fuer reine Autosave-Formulare (jedes
   *  Feld schreibt einzeln per `onBlur`/`onChange`, kein Sammel-Speichern-Knopf) — unterdrueckt
   *  die "Ungespeicherte Änderungen verwerfen?"-Warnung, die dort immer ein Fehlalarm waere.
   *  Vorgabe `false`: unveraendertes Verhalten fuer alle bestehenden Aufrufer. */
  autosave?: boolean;
}) {
  const guard = useCloseGuard(open, onClose, autosave);
  useOverlayOpenSignal(open);
  if (!open) return null;
  return (
    // Mobile-Fix (27.08.2026, Antoine: „Speicher-Button verschwindet beim Scrollen"): `inset-y-0`
    // auf einem `position:fixed`-Element rechnet in mobilen Browsern gegen den GROSSEN Viewport
    // (so, als waere die Adressleiste bereits eingeklappt) — waehrend sie sichtbar ist, ragt der
    // untere Rand ueber den tatsaechlich sichtbaren Bereich hinaus, die Fuszeile mit dem
    // Speichern-Knopf liegt darunter verdeckt. Blendet der Browser die Adressleiste beim Scrollen
    // aus, wird der Viewport ploetzlich groesser und der Knopf "erscheint" — genau das beschriebene
    // Flackern. `h-dvh` (dynamic viewport height, Tailwind 3.4+) folgt statt dessen dem TATSAECHLICH
    // sichtbaren Bereich.
    <div className="fixed inset-x-0 top-0 z-[60] h-dvh lg:right-[var(--copilot-pinned-w,0px)]">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[3px]" onClick={guard.requestClose} aria-hidden />
      <aside
        className={`absolute inset-y-0 right-0 flex w-full flex-col bg-surface-container-lowest shadow-2xl dark:bg-night-card ${DRAWER_BREITE[breite]}`}
        onChangeCapture={guard.onChangeCapture}
      >
        <header className="border-b border-outline-variant/30 px-6 py-4 dark:border-outline/30">
          <div className="flex items-center gap-3">
            <h2 className="min-w-0 flex-1 truncate font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
              {title}
            </h2>
            {headerAccessory}
            <Button variant="ghost" className="shrink-0 !px-2" onClick={guard.requestClose} aria-label="Schließen">
              <Icon name="close" />
            </Button>
          </div>
          {subtitle != null && (
            <p className="mt-1 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{subtitle}</p>
          )}
        </header>
        {/* overflow-x-hidden: gleiches Sicherheitsnetz wie html/body in index.css (dort mit
            Begruendung) — ein Grid-/Flex-Kind ohne min-w-0 darf diesen Container sonst horizontal
            aufreissen. */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-5">{children}</div>
        {footer != null && <footer className={FUSSLEISTE}>{footer}</footer>}
      </aside>
      {guard.dialog}
    </div>
  );
}

const DRAWER_BREITE = { schmal: "max-w-[440px]", mittel: "max-w-lg", breit: "max-w-[760px]" } as const;

/** Eine Fußleiste für Drawer und Modal: Trennlinie, Knöpfe rechtsbündig, der primäre ganz rechts
 *  (Aufrufer setzen „Abbrechen“ zuerst, die Hauptaktion zuletzt). */
const FUSSLEISTE =
  "flex shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-outline-variant/30 px-6 py-4 dark:border-outline/30";

/** Zentrierter Dialog — im Unterschied zum Drawer NICHT seitlich einfahrend, sondern eine mittig
 *  platzierte Karte über einem abgedunkelten Hintergrund.
 *
 *  Design-Durchsicht 26.09.2026, Entscheidung 4: EIN Gerüst für alle Dialoge — Titelzeile mit
 *  Schließen-X (`title`, optional `subtitle`), scrollbarer, gepolsterter Inhalt (`children`) und
 *  feste Fußleiste (`footer`, Knöpfe rechtsbündig, primär rechts). Vorher brachte jeder Aufrufer
 *  sein eigenes Innenleben mit — mal ohne Innenabstand, mal ohne X, Knöpfe mal links, mal rechts.
 *  `onSubmit` macht die Karte zu einem `<form>`, damit ein `type="submit"`-Knopf in der Fußleiste
 *  und die Eingabetaste im Inhalt wie bisher absenden. */
export function Modal({
  open,
  onClose,
  width = "600px",
  title,
  subtitle,
  footer,
  onSubmit,
  gefahr = false,
  oben = false,
  testId,
  children,
  autosave = false,
}: {
  open: boolean;
  onClose: () => void;
  width?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  /** Titel im Fehler-Ton (endgültiges Löschen). */
  gefahr?: boolean;
  /** Nur für `useBestaetigung`: liegt über einem schon offenen Drawer/Modal. */
  oben?: boolean;
  /** `data-testid` an der ganzen Karte (Titel, Inhalt und Fußleiste). */
  testId?: string;
  children?: ReactNode;
  /** s. `Drawer`/`useCloseGuard` oben — bisher hat kein `Modal`-Aufrufer ein reines
   *  Autosave-Formular (Stand 01.09.2026, Repo-weit geprueft), der Prop steht aus
   *  API-Symmetrie und fuer kuenftige Faelle bereit. Vorgabe `false`: unveraendert. */
  autosave?: boolean;
}) {
  const guard = useCloseGuard(open, onClose, autosave);
  const titelId = useId();
  useOverlayOpenSignal(open);
  if (!open) return null;
  // Nur als `form`, wenn abgesendet wird; der Typ ist für beide Elemente gleich brauchbar.
  const Karte = (onSubmit ? "form" : "div") as "form";
  return (
    // Mobile-Fix (27.08.2026, s. Drawer oben fuer die volle Herleitung): `h-dvh` statt `inset-0`
    // (das rechnet als `position:fixed` gegen den GROSSEN statt den sichtbaren Viewport).
    // Handy-Fix (26.09.2026, Design-Review): `width` ist fest (600px o. ae.) — ohne `max-w-full`
    // lief der Dialog auf 390px rechts aus dem Bild, die Fußknöpfe waren unerreichbar. `grid-cols-1`
    // (minmax(0,1fr)) ist nötig, sonst wächst die implizite Spur mit und `max-w-full` greift nicht. Rand auf dem
    // Handy 16px statt 24px (= calc(100vw - 32px)). Titel und Fußleiste bleiben stehen, nur der
    // Inhalt scrollt. Ab `sm` ist alles wie vorher.
    <div className={`fixed inset-x-0 top-0 grid h-dvh grid-cols-1 place-items-center p-4 sm:p-6 ${oben ? "z-[70]" : "z-[60]"}`}>
      <div className="absolute inset-0 bg-black/45" onClick={guard.requestClose} aria-hidden />
      <Karte
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        data-testid={testId}
        className="relative flex max-h-full max-w-full flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl dark:bg-night-card"
        style={{ width }}
        onChangeCapture={guard.onChangeCapture}
        onSubmit={onSubmit}
      >
        <header className="flex shrink-0 items-start gap-3 px-6 pb-3 pt-5">
          <div className="min-w-0 flex-1">
            <h2
              id={titelId}
              className={`font-headline-sm text-headline-sm font-bold ${gefahr ? "text-error" : "text-on-background dark:text-inverse-on-surface"}`}
            >
              {title}
            </h2>
            {subtitle != null && (
              <p className="mt-1 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{subtitle}</p>
            )}
          </div>
          <Button type="button" variant="ghost" className="-mr-2 -mt-1 shrink-0 !px-2" onClick={guard.requestClose} aria-label="Dialog schließen">
            <Icon name="close" />
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 pb-5">{children}</div>
        {footer != null && <footer className={FUSSLEISTE}>{footer}</footer>}
      </Karte>
      {guard.dialog}
    </div>
  );
}

/**
 * UX-Paket 26.08.2026, Punkt 1: sichtbarer Zeichenzähler unter Textinputs/-areas mit `maxLength`
 * (z. B. `<Zeichenzaehler current={text.length} max={1000} />` direkt unter dem Feld). Wird das
 * Limit erreicht, färbt sich der Zähler rot — reiner Hinweis, das Feld selbst blockiert weitere
 * Eingabe schon über das native `maxLength`-Attribut (kein serverseitiger Grund fürs Scheitern
 * mehr, der sich nur beim Absenden zeigt).
 */
export function Zeichenzaehler({ current, max }: { current: number; max: number }) {
  const amLimit = current >= max;
  return (
    <span className={`mt-1 block text-right font-label-md text-[11px] ${amLimit ? "font-semibold text-error" : "text-secondary dark:text-secondary-fixed-dim"}`}>
      {current}/{max}
    </span>
  );
}

/**
 * UX-Paket 26.08.2026, Punkt 2: Ersatz für `<input type="time">`. Der native Zeit-Picker zeigt
 * je nach Browser-/Betriebssystem-Spracheinstellung ein AM/PM-Format (v. a. auf Windows mit
 * en-US-Locale) — das entscheidet ausschließlich der Browser, kein CSS/Attribut erzwingt 24h
 * plattformübergreifend zuverlässig. Ersatz: ein reiner Text-Input mit selbst erzwungenem
 * `HH:mm`-Format, identisch auf jedem Gerät. Bleibt `name`-basiert (FormData-kompatibel), damit
 * bestehende uncontrolled Formulare (wie das Eckdaten-Bearbeiten-Drawer) unverändert funktionieren.
 */
export function ZeitEingabe({
  name,
  defaultValue,
  className = "",
  onValueChange,
}: {
  name: string;
  defaultValue?: string;
  className?: string;
  /** Optional: fürs Inline-Editing (Punkt 4) — Elternkomponente will den aktuellen Wert kennen,
   *  ohne dass das Feld deshalb zu einem klassischen `value`/`onChange`-controlled Input wird
   *  (Cursor-Sprünge beim Autoformat wären sonst Sache des Aufrufers, nicht dieser Komponente). */
  onValueChange?: (value: string) => void;
}) {
  const [value, setValue] = useState(defaultValue ?? "");

  function melde(next: string) {
    setValue(next);
    onValueChange?.(next);
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const ziffern = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
    melde(ziffern.length > 2 ? `${ziffern.slice(0, 2)}:${ziffern.slice(2)}` : ziffern);
  }

  /** Beim Verlassen des Felds normalisieren/kappen (z. B. "9:7" -> "09:07", "25:99" -> "23:59") -
   *  während des Tippens NICHT eingreifen, sonst springt der Cursor bei jeder Ziffer. */
  function onBlur(e: FocusEvent<HTMLInputElement>) {
    const roh = e.target.value;
    if (!roh) return;
    const m = roh.match(/^(\d{1,2}):?(\d{0,2})$/);
    if (!m) {
      melde("");
      return;
    }
    const hh = Math.min(23, parseInt(m[1] || "0", 10));
    const mm = Math.min(59, parseInt(m[2] || "0", 10));
    melde(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      name={name}
      placeholder="HH:mm"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      maxLength={5}
      className={`input${className ? ` ${className}` : ""}`}
    />
  );
}

/**
 * Der EINE Leerzustand (Design-Durchsicht Entscheidung 5, Antoine 26.09.2026). Vorher gab es zwei
 * Muster nebeneinander — den grossen orangen Kreis und den kleinen neutralen (`kompakt`). Jetzt
 * immer: neutraler 40-px-Kreis mit Symbol, ein Satz „was ist hier“ (`title`), ein Satz „was tun“
 * (`hint`), optional ein Knopf. Der Knopf ist bewusst `secondary`: der orange Hauptknopf gehoert
 * dem Seitenkopf (Entscheidung 9, ein oranger Hauptknopf je Bereich). `children` nimmt seltene
 * Zusatzinhalte auf (z. B. mehrere Auswege), ohne dass die Seite einen eigenen Kreis baut.
 */
export function EmptyState({
  icon,
  title,
  hint,
  cta,
  tip,
  link,
  andereTreffer,
  children,
}: {
  icon: string;
  title: string;
  hint?: string;
  cta?: { label: string; onClick: () => void; icon?: string };
  tip?: ReactNode;
  /** Listenstandard (Brief § 5): Accent-Textlink, z. B. „Alle Buchungen anzeigen“ im leeren Reiter. */
  link?: { label: string; onClick: () => void } | { label: string; onClick: () => void }[];
  /**
   * Punkt 115 (Paket 1 „Nie mehr ins Leere“): Treffer in ANDEREN Reitern, z. B. bei aktiver Suche
   * „1 Treffer unter Bezahlt — anzeigen“. Eintraege mit `anzahl` 0 werden nicht gezeigt.
   */
  andereTreffer?: { label: string; anzahl: number; onClick: () => void }[];
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-5 py-16 text-center">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F6F1E7] text-[#6F675A] dark:bg-white/[0.06] dark:text-[#b8ae9e]">
        <Icon name={icon} className="text-[20px]" />
      </div>
      <div>
        <p className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">{title}</p>
        {hint && (
          <p className="mx-auto mt-1.5 max-w-sm font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
            {hint}
          </p>
        )}
      </div>
      {andereTreffer?.some((t) => t.anzahl > 0) && (
        <ul className="flex flex-col items-center gap-1">
          {andereTreffer
            .filter((t) => t.anzahl > 0)
            .map((t) => (
              <li key={t.label}>
                <button
                  type="button"
                  onClick={t.onClick}
                  className="rounded-md text-[12.5px] font-semibold text-primary hover:underline dark:text-primary-fixed"
                >
                  {t.anzahl} Treffer unter {t.label} — anzeigen
                </button>
              </li>
            ))}
        </ul>
      )}
      {link && (!Array.isArray(link) || link.length > 0) && (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {(Array.isArray(link) ? link : [link]).map((l) => (
            <button
              key={l.label}
              type="button"
              onClick={l.onClick}
              className="rounded-md text-[12.5px] font-semibold text-primary hover:underline dark:text-primary-fixed"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
      {cta && (
        <button type="button" className="btn-secondary" onClick={cta.onClick}>
          {cta.icon && <Icon name={cta.icon} className="text-[16px]" />} {cta.label}
        </button>
      )}
      {children}
      {tip && (
        <div className="flex items-center gap-1.5 rounded-lg border border-surface-variant bg-surface-container-low px-3 py-2 font-body-sm text-[11.5px] text-secondary-fixed-dim dark:border-outline/30 dark:bg-surface-variant/10">
          <Icon name="info" className="text-[14px]" /> {tip}
        </div>
      )}
    </div>
  );
}

/** I3: Karten-Skeleton (Avatar + 2 Zeilen + Pill) statt schlichter Pulse-Balken.
 *
 *  Listenstandard (Brief § 5): mit `spalten` entsteht stattdessen ein `<tbody>` im Spaltenraster
 *  der Tabelle — je Spalte ein Balken, `"pill"` als 84×20-Pille, `"leer"` ohne Inhalt (z. B.
 *  Aktionsspalte). Kopf, Reiter und Fußzeile der Liste bleiben dabei sichtbar, keine Spinner. */
export function LoadingRows({ rows = 4, spalten }: { rows?: number; spalten?: readonly ("text" | "pill" | "leer")[] }) {
  if (spalten) {
    const flaechen = ["bg-[#F0EAE0]", "bg-[#F2ECDF]", "bg-[#F6F1E7]"];
    return (
      <tbody aria-busy="true" data-testid="liste-laedt">
        {Array.from({ length: rows }, (_, i) => (
          <tr key={i} className="animate-pulse border-t border-[#F6F0E4] dark:border-white/[0.06]">
            {spalten.map((art, j) => (
              <td key={j} className="h-[54px] px-[7px] first:pl-[18px] last:pr-[18px]">
                {art === "pill" ? (
                  <div className="h-5 w-[84px] rounded-full bg-[#F0EAE0] dark:bg-white/[0.08]" />
                ) : art === "text" ? (
                  <div
                    className={`h-2.5 rounded-[5px] ${flaechen[(i + j) % 3]} dark:bg-white/[0.06]`}
                    style={{ width: `${55 + ((i * 7 + j * 13) % 40)}%` }}
                  />
                ) : null}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  }
  return (
    <div className="divide-y divide-surface-variant/60 dark:divide-outline/20" aria-busy>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex animate-pulse items-center gap-3.5 px-4 py-4">
          <div className="h-9 w-9 shrink-0 rounded-full bg-surface-variant dark:bg-white/[0.08]" />
          <div className="flex-1 space-y-2">
            <div className="h-2.5 w-[45%] rounded bg-surface-variant dark:bg-white/[0.08]" />
            <div className="h-2 w-[68%] rounded bg-surface-container-high dark:bg-white/[0.05]" />
          </div>
          <div className="h-5 w-16 shrink-0 rounded-full bg-surface-container-high dark:bg-white/[0.05]" />
        </div>
      ))}
    </div>
  );
}

/** I2: Icon-Kachel + Titel + optionaler Retry-Button + optional Fehlercode/Zeit (Design-Vorlage 3i).
 *
 *  Listenstandard (Brief § 5): `zentriert` zeigt den Fehler mittig im Listenbereich
 *  (`role="alert"`, roter Kreis mit Ausrufezeichen, Sekundärknopf „Erneut versuchen“). */
export function ErrorNote({
  message,
  title = "Etwas ist schiefgelaufen",
  meta,
  onRetry,
  zentriert = false,
}: {
  message: string;
  title?: string;
  meta?: string;
  onRetry?: () => void;
  zentriert?: boolean;
}) {
  if (zentriert) {
    return (
      <div role="alert" className="flex flex-col items-center justify-center gap-3 px-5 py-16 text-center">
        <div className="grid h-10 w-10 place-items-center rounded-full bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/22 dark:text-[#e39b92]">
          <Icon name="priority_high" className="text-[20px]" />
        </div>
        <div>
          <p className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">{title}</p>
          <p className="mx-auto mt-1 max-w-md text-[12.5px] leading-relaxed text-[#6F675A] dark:text-[#b8ae9e]">{message}</p>
          {meta && <p className="mt-1 text-[11.5px] text-[#9A9080]">{meta}</p>}
        </div>
        {onRetry && (
          <Button type="button" variant="secondary" className="!h-8 !rounded-[9px] !py-0 !text-[12.5px]" onClick={onRetry}>
            Erneut versuchen
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="m-4 flex items-start gap-3 rounded-xl border border-[#F1D2CB] bg-[#FBEDEA] p-4 dark:border-[#B23B32]/30 dark:bg-[#B23B32]/10">
      <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[9px] bg-[#F5DAD3] text-[#B23B32] dark:bg-[#B23B32]/25 dark:text-[#e39b92]">
        <Icon name="error" className="text-[18px]" />
      </div>
      <div className="flex-1">
        <p className="font-body-sm text-[14px] font-bold text-[#8F2E26] dark:text-[#e39b92]">{title}</p>
        <p className="mt-0.5 font-body-sm text-[12px] leading-relaxed text-[#9A5049] dark:text-[#e39b92]/80">{message}</p>
        {(onRetry || meta) && (
          <div className="mt-3 flex items-center gap-2.5">
            {onRetry && (
              <button type="button" className="btn-primary !py-2 !text-[12px]" onClick={onRetry}>
                <Icon name="refresh" className="text-[14px]" /> Erneut versuchen
              </button>
            )}
            {meta && <span className="font-body-sm text-[11.5px] text-[#9A5049]/90">{meta}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Listenstandard (Brief § 2.3): Leerwert „–“ in einer Tabellenzelle — heller Strich, für
 * Screenreader „keine Angabe“ statt eines vorgelesenen Gedankenstrichs.
 */
export function KeineAngabe() {
  return (
    <span role="img" aria-label="keine Angabe" className="text-[#CFC5B3] dark:text-white/25">
      –
    </span>
  );
}

/**
 * Listenstandard (Brief § 3, WARTESCHLANGE Punkt 95): sortierbarer Spaltenkopf — `<th>` mit
 * `aria-sort`, darin ein echter `<button>`. Inaktiv: Doppelpfeil in Deko-Farbe; aktiv: Pfeil im
 * getönten Kästchen in Akzentfarbe. Sticky (`top-0`) innerhalb des Scrollbereichs der Liste,
 * über dem Zeilen-Overlay-Link (`z-[1]`) und den Zeilenaktionen (`z-[2]`).
 */
export function SortierKopf({
  label,
  aktiv,
  onClick,
  rechts = false,
  als: Tag = "th",
}: {
  label: string;
  /** Aktuelle Richtung dieser Spalte, `null` = nicht sortiert. */
  aktiv: "asc" | "desc" | null;
  onClick: () => void;
  /** Numerische Spalten: Kopf rechtsbündig (Brief § 2.2). */
  rechts?: boolean;
  /** `"div"` für Listen im CSS-Grid statt `<table>` (z. B. aufklappbare Gruppen der Zahlungen). */
  als?: "th" | "div";
}) {
  return (
    <Tag
      role="columnheader"
      aria-sort={aktiv === "asc" ? "ascending" : aktiv === "desc" ? "descending" : "none"}
      className={`${LISTEN_KOPF_ZELLE} ${rechts ? "text-right" : "text-left"}${Tag === "div" ? ` flex items-center${rechts ? " justify-end" : ""}` : ""}`}
    >
      <button
        type="button"
        onClick={onClick}
        title={`Nach ${label} sortieren`}
        className={`group inline-flex items-center gap-1.5 whitespace-nowrap rounded-md uppercase tracking-[0.07em] transition-colors hover:text-[#262119] focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#C2410C] focus-visible:outline-none dark:hover:text-inverse-on-surface dark:focus-visible:shadow-[0_0_0_2px_#262119,0_0_0_4px_#C2410C] ${
          aktiv ? "text-[#262119] dark:text-inverse-on-surface" : ""
        }`}
      >
        {label}
        {aktiv ? (
          <span
            aria-hidden="true"
            className="grid h-4 w-4 place-items-center rounded-[5px] bg-primary/[0.12] text-primary dark:bg-primary/25 dark:text-primary-fixed"
          >
            <Icon name={aktiv === "asc" ? "arrow_upward" : "arrow_downward"} className="text-[11px]" />
          </span>
        ) : (
          <span aria-hidden="true" className="inline-flex">
            <Icon name="unfold_more" className="text-[13px] text-[#CFC5B3] dark:text-white/25" />
          </span>
        )}
      </button>
    </Tag>
  );
}

/** Gemeinsame Klassen jeder Kopfzelle einer Standardliste (auch der nicht sortierbaren, z. B.
 *  der Erinnerungs-Spalte ohne sichtbare Überschrift). Deckende Fläche, damit beim Scrollen
 *  keine Zeile durch den sticky Kopf scheint; die Unterkante als Schatten, weil ein Rand in
 *  `border-collapse`-Tabellen nicht mitklebt. */
export const LISTEN_KOPF_ZELLE =
  "sticky top-0 z-[3] h-10 whitespace-nowrap bg-[#FBF8F1] px-[7px] text-[10.5px] font-semibold text-[#9A9080] shadow-[inset_0_-1px_0_#F2ECDF] first:pl-[18px] last:pr-[18px] dark:bg-[#2B251D] dark:text-secondary-fixed-dim dark:shadow-[inset_0_-1px_0_rgba(255,255,255,0.08)]";

/**
 * Listenstandard (Brief § 9): Sortierung als Menü für Listen OHNE Spaltenköpfe (Posteingang,
 * Kachel- und Kartenlisten). Die erste Option mit `key: null` ist die Standardreihenfolge;
 * gemerkt wird über `useListSort` (URL vor localStorage) wie bei den Spaltenköpfen.
 */
export function SortierMenue({
  optionen,
  sortKey,
  dir,
  onWahl,
  className = "",
}: {
  optionen: readonly { label: string; key: string | null; dir?: "asc" | "desc" }[];
  sortKey: string | null;
  dir: "asc" | "desc" | null;
  onWahl: (wert: { key: string; dir: "asc" | "desc" } | null) => void;
  className?: string;
}) {
  const wert = (o: { key: string | null; dir?: "asc" | "desc" }) => (o.key ? `${o.key}:${o.dir ?? "asc"}` : "standard");
  const aktuell = optionen.find((o) => o.key === sortKey && (o.key == null || (o.dir ?? "asc") === dir)) ?? optionen[0];
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span aria-hidden="true" className="inline-flex text-[#6F675A] dark:text-[#b8ae9e]">
        <Icon name="sort" className="text-[18px]" />
      </span>
      <div className="min-w-[180px]">
        <Select
          ariaLabel="Sortierung"
          value={aktuell ? wert(aktuell) : "standard"}
          onValueChange={(v) => {
            const o = optionen.find((x) => wert(x) === v);
            onWahl(o?.key ? { key: o.key, dir: o.dir ?? "asc" } : null);
          }}
          options={optionen.map((o) => ({ value: wert(o), label: o.label }))}
        />
      </div>
    </div>
  );
}

/**
 * Listenstandard (Brief § 2.1) + Punkt 115 (Paket 3): EIN Baustein für die Status-Reiter über
 * einer Liste (Zahlungen, Buchungen, Posteingang — vorher dreimal von Hand gebaut). Die Zähler
 * reicht die Seite herein; bei aktiver Suche sind das die Suchtreffer je Reiter (G1).
 */
export function ListenReiter<K extends string>({
  label,
  reiter,
  aktiv,
  onWahl,
}: {
  /** `aria-label` der Reiterleiste, z. B. „Zahlungen nach Status“. */
  label: string;
  reiter: readonly { key: K; label: string; anzahl: number }[];
  aktiv: K;
  onWahl: (key: K) => void;
}) {
  const { ref, rand } = useReiterScroll<HTMLDivElement>(aktiv);
  return (
    <div className="relative">
      <div
        ref={ref}
        role="tablist"
        aria-label={label}
        data-rand={rand || undefined}
        className="tab-scroll flex gap-[26px] border-b border-[#E6DECE] px-[18px] dark:border-white/[0.08]"
      >
        {reiter.map((r) => (
          <button
            key={r.key}
            type="button"
            role="tab"
            aria-selected={aktiv === r.key}
            onClick={() => onWahl(r.key)}
            className={`h-[46px] whitespace-nowrap text-[13px] transition-colors ${
              aktiv === r.key
                ? "font-semibold text-primary shadow-[inset_0_-2px_0_#C2410C] dark:text-primary-fixed"
                : "font-medium text-[#57503F] hover:text-[#262119] dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
            }`}
          >
            {r.label} <span className="ml-0.5 font-medium text-[#9A9080]">{r.anzahl.toLocaleString("de-DE")}</span>
          </button>
        ))}
      </div>
      <ReiterRandPfeile rand={rand} />
    </div>
  );
}

/**
 * Reiterleiste auf dem Handy (Handy-Mockup 26.09.2026, Punkt 6): der aktive Reiter rückt in die
 * Mitte — beim Antippen und beim Öffnen der Seite —, und `rand` sagt, auf welcher Seite noch Reiter
 * versteckt liegen. `.tab-scroll[data-rand]` blendet dort per Maske aus, `ReiterRandPfeile` zeigt
 * den kleinen Pfeil. Passt alles in die Breite (Desktop), bleibt `rand` leer und nichts ändert sich.
 * Den aktiven Reiter findet der Hook selbst (`aria-selected`/`data-state=active`).
 */
export type ReiterRand = "" | "links" | "rechts" | "beide";
export function useReiterScroll<T extends HTMLElement>(aktiv: string) {
  const ref = useRef<T>(null);
  const [rand, setRand] = useState<ReiterRand>("");
  const erstesMal = useRef(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const messen = () => {
      const links = el.scrollLeft > 2;
      const rechts = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      setRand(links && rechts ? "beide" : links ? "links" : rechts ? "rechts" : "");
    };
    messen();
    el.addEventListener("scroll", messen, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(messen) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", messen);
      ro?.disconnect();
    };
  }, []);
  useEffect(() => {
    const el = ref.current;
    const a = el?.querySelector<HTMLElement>('[aria-selected="true"], [data-state="active"]');
    if (!el || !a || el.scrollWidth <= el.clientWidth) return;
    const er = el.getBoundingClientRect();
    const ar = a.getBoundingClientRect();
    const ruhig = erstesMal.current || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo?.({ left: el.scrollLeft + ar.left - er.left - (er.width - ar.width) / 2, behavior: ruhig ? "auto" : "smooth" });
    erstesMal.current = false;
  }, [aktiv]);
  return { ref, rand };
}

/** Kleiner Pfeil am Rand einer Reiterleiste, nur auf der Seite, auf der noch etwas kommt. Rein
 *  dekorativ (`aria-hidden`, keine Klickfläche) — gewischt wird die Leiste selbst. */
export function ReiterRandPfeile({ rand }: { rand: ReiterRand }) {
  const pfeil = "pointer-events-none absolute top-0 flex h-full items-center text-[#9A9080] dark:text-secondary-fixed-dim";
  return (
    <>
      {(rand === "links" || rand === "beide") && (
        <span aria-hidden="true" data-testid="reiter-rand-links" className={`${pfeil} left-0`}>
          <Icon name="chevron_left" className="text-[16px]" />
        </span>
      )}
      {(rand === "rechts" || rand === "beide") && (
        <span aria-hidden="true" data-testid="reiter-rand-rechts" className={`${pfeil} right-0`}>
          <Icon name="chevron_right" className="text-[16px]" />
        </span>
      )}
    </>
  );
}

/**
 * Listenstandard (Brief § 2.5): Fußzeile einer Liste — links „{n} von {gesamt} {einheit}“ und
 * optional die Summe der sichtbaren Zeilen, rechts der Sortierhinweis (`aria-live="polite"`)
 * mit „Zurücksetzen“.
 */
export function ListFooter({
  anzahl,
  gesamt,
  einheit,
  summe,
  zusatz,
  sortiertNach,
  richtung,
  onReset,
  ohneSortierHinweis = false,
}: {
  anzahl: number;
  gesamt: number;
  /** Plural, z. B. „Buchungen“. */
  einheit: string;
  /** Fertig formatierter Betrag; weglassen bei Listen ohne Beträge. */
  summe?: string;
  /** Weitere Angabe links hinter der Summe, z. B. „Top-Kunde“ der Kundenliste. */
  zusatz?: ReactNode;
  /** Label der Sortierspalte, `null` = Standardreihenfolge. */
  sortiertNach: string | null;
  richtung: "asc" | "desc" | null;
  onReset: () => void;
  /** Handy-Karten ohne Spaltenköpfe: kein „Spaltenkopf anklicken zum Sortieren“. */
  ohneSortierHinweis?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-[#EFE8DA] bg-[#FBF8F1] px-[18px] py-3 text-[12px] text-[#6F675A] dark:border-white/[0.08] dark:bg-[#2B251D] dark:text-[#b8ae9e]">
      <span className="tabular-nums">
        {anzahl.toLocaleString("de-DE")} von {gesamt.toLocaleString("de-DE")} {einheit}
        {summe != null && (
          <>
            {" · "}Summe <strong className="font-semibold text-[#262119] dark:text-inverse-on-surface">{summe}</strong>
          </>
        )}
        {zusatz != null && <> · {zusatz}</>}
      </span>
      <span className="flex items-center gap-2">
        <span aria-live="polite">
          {sortiertNach ? (
            <>
              Sortiert nach <strong className="font-semibold text-[#262119] dark:text-inverse-on-surface">{sortiertNach}</strong>
              {" · "}
              {richtung === "desc" ? "absteigend" : "aufsteigend"}
            </>
          ) : ohneSortierHinweis ? null : (
            <span className="text-[#9A9080]">Spaltenkopf anklicken zum Sortieren</span>
          )}
        </span>
        {sortiertNach && (
          <button type="button" onClick={onReset} className="rounded-md font-semibold text-primary hover:underline dark:text-primary-fixed">
            Zurücksetzen
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Rueckweg (Punkt 115, Paket 2; Antoine G2/G3 vom 24.09.2026): EIN Baustein fuer die Zeile ueber dem
 * Seitentitel, statt der fuenf frueheren Eigenbauten (Chevron-Breadcrumb, Punkt-Breadcrumb,
 * „← Karten“, Knopf „← Zurück“, Knopf „← Zurück zum Auftrag“).
 *  - `eltern`: fester Breadcrumb, hoechstens zwei Ebenen („Kunden › Debora Ribeiro“). Funktioniert
 *    auch bei direkt geoeffneten Links und in neuen Tabs.
 *  - Herkunft: steht `?von=` in der Adresse (s. `lib/rueckweg.ts`), kommt „← Zurück zu <vonName>“
 *    davor — ausser sie zeigt ohnehin auf dieselbe Elternseite.
 * Muss innerhalb eines Routers stehen; `PageHeader` prueft das selbst.
 */
export function Rueckweg({
  eltern,
  aktuell,
  className = "mb-3",
}: {
  eltern?: { label: string; to: string };
  aktuell?: ReactNode;
  /** Abstand/Einbettung; Vorgabe `mb-3` fuer die Zeile ueber einem Titel. */
  className?: string;
}) {
  const herkunft = useHerkunft();
  const zeigeHerkunft = herkunft != null && herkunft.to !== eltern?.to;
  if (!eltern && !zeigeHerkunft) return null;
  return (
    <nav aria-label="Rückweg" className={`${className} flex flex-wrap items-center gap-x-3 gap-y-1 font-body-sm text-body-sm`}>
      {zeigeHerkunft && (
        <Link
          to={herkunft.to}
          data-testid="rueckweg-herkunft"
          className="inline-flex items-center gap-1 rounded-md font-semibold text-primary hover:underline dark:text-primary-fixed"
        >
          <Icon name="arrow_back" className="text-[16px]" /> Zurück{herkunft.name ? ` zu ${herkunft.name}` : ""}
        </Link>
      )}
      {zeigeHerkunft && eltern && <span aria-hidden className="h-1 w-1 rounded-full bg-outline-variant" />}
      {eltern && (
        <span className="flex min-w-0 items-center gap-1.5">
          <Link to={eltern.to} data-testid="rueckweg-eltern" className="shrink-0 text-secondary hover:text-primary dark:text-secondary-fixed-dim">
            {eltern.label}
          </Link>
          {aktuell != null && (
            <>
              <Icon name="chevron_right" className="shrink-0 text-[14px] text-secondary-fixed-dim" />
              <span className="truncate font-semibold text-on-background dark:text-inverse-on-surface" data-testid="rueckweg-aktuell">
                {aktuell}
              </span>
            </>
          )}
        </span>
      )}
    </nav>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  zurueck,
  aktuell,
}: {
  /** Meist Text; der Angebots-Builder setzt einen verlinkten Kundennamen hinein (Mockup v3). */
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Fester Breadcrumb ueber dem Titel (`Rueckweg`), z. B. `{ label: "Kunden", to: "/kunden" }`. */
  zurueck?: { label: string; to: string };
  /** Zweite Breadcrumb-Ebene; ohne Angabe der Titel. */
  aktuell?: ReactNode;
}) {
  const imRouter = useInRouterContext();
  return (
    <>
      {imRouter && <Rueckweg eltern={zurueck} aktuell={zurueck ? (aktuell ?? title) : undefined} />}
      <div data-testid="seitenkopf" className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display-lg text-display-lg text-on-background dark:text-inverse-on-surface">{title}</h1>
          {subtitle != null && (
            <div className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{subtitle}</div>
          )}
        </div>
        {actions != null && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
      </div>
    </>
  );
}

/**
 * Button: reiner Wrapper um die Bestand-Klassen `btn-primary`/`btn-secondary`/`btn-ghost`
 * (`index.css` § "Wiederverwendbare Bausteine im Stitch-Idiom") — Design-Komponenten-Konzept
 * (`docs/KONZEPT-DESIGN-KOMPONENTEN.md` § "Architektur-Entscheidung" Punkt 1): kein neues
 * visuelles Design, `variant="primary"` (Vorgabe) rendert exakt dieselbe Klasse wie die heute
 * ueberall im Code verstreuten `className="btn-primary"`-Stellen. Reicht alle nativen
 * `<button>`-Attribute durch (`type`, `disabled`, `onClick`, ...) statt sie nachzubauen —
 * `className` haengt zusaetzliche Klassen an, wie es der Bestand bereits tut (z. B.
 * `className="btn-primary !py-1.5 ml-auto"` in `customers.tsx`), statt sie zu ersetzen.
 *
 * P0 dieses Konzepts ist rein additiv: kein bestehender `btn-*`-Aufrufer wird hier umgestellt
 * (das ist P2, seitenweise, mit eigener Ratsche).
 */
/** Klassen ausgeschrieben statt `btn-${variant}`: Tailwind erkennt nur wörtlich im Quelltext
 *  stehende Klassen — `btn-danger` kam sonst nirgends vor und fehlte im gebauten CSS (der
 *  Gefahrknopf der Rückfragen war farblos). */
const KNOPF_KLASSE = { primary: "btn-primary", secondary: "btn-secondary", ghost: "btn-ghost", danger: "btn-danger" } as const;

export function Button({
  variant = "primary",
  className = "",
  ...rest
}: {
  /** `danger` (E9): nur fuer Loeschen — Ziegelton `error`, nie fuer umkehrbare Aktionen. */
  variant?: keyof typeof KNOPF_KLASSE;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`${KNOPF_KLASSE[variant]}${className ? ` ${className}` : ""}`} {...rest} />;
}

/**
 * ButtonLink: dieselbe Optik wie `Button`, aber ein echter Link.
 *
 * Angelegt am 19.09.2026 fuer den Signaturlink der gast-ausgeloesten Kostenuebernahme (Punkt 57
 * Etappe 3): der Gast wird auf eine fremde Seite geschickt, das ist eine Navigation und kein
 * Schalter. Ein `<button>` mit `window.open` waere semantisch falsch (kein Mittelklick, kein
 * „in neuem Tab oeffnen", fuer Screenreader ein Bedienelement statt eines Ziels), ein rohes
 * `<a className="btn-primary …>` in der Seite haette den Bestand an rohem Markup erhoeht — und
 * das Ziel dieser Bausteine ist 0 (docs/KONZEPT-DESIGN-KOMPONENTEN.md § P0).
 *
 * Reicht alle nativen `<a>`-Attribute durch. Mit `target="_blank"` gehoert `rel="noopener"` dazu;
 * das bleibt bewusst Sache des Aufrufers, damit hier keine stille Magie passiert.
 */
export function ButtonLink({
  variant = "primary",
  className = "",
  ...rest
}: {
  variant?: "primary" | "secondary" | "ghost";
} & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a className={`btn-${variant}${className ? ` ${className}` : ""}`} {...rest} />;
}

/**
 * ButtonRouterLink: `Button`-Optik auf einem react-router-`<Link>` (klientseitige Navigation, kein
 * Seitenneuladen). Wie `ButtonLink`, nur fuer interne Ziele; reicht alle `Link`-Props durch.
 */
export function ButtonRouterLink({
  variant = "primary",
  className = "",
  ...rest
}: {
  variant?: "primary" | "secondary" | "ghost";
  className?: string;
} & Omit<LinkProps, "className">) {
  return <Link className={`btn-${variant}${className ? ` ${className}` : ""}`} {...rest} />;
}

/**
 * ButtonOptik: `Button`-Optik auf einem `<label>` (Dateiauswahl: das versteckte `<input type="file">`
 * steht als Kind darin) oder einem `<span>` (reine Anzeige innerhalb eines klickbaren Elternteils).
 * Element und alle Attribute bleiben wie beim rohen Markup.
 */
export function ButtonOptik({
  as: Tag = "span",
  variant = "secondary",
  className = "",
  ...rest
}: {
  as?: "span" | "label";
  variant?: "primary" | "secondary" | "ghost";
} & HTMLAttributes<HTMLElement>) {
  return <Tag className={`btn-${variant}${className ? ` ${className}` : ""}`} {...rest} />;
}

/**
 * InputOptik: `.input`-Optik auf einem `<div>` — reine Anzeige in Eingabefeld-Optik ohne
 * Eingabeelement (z. B. ein fest vorgegebener Wert im Formular). Ein `Input readOnly` waere
 * fokussierbar und wuerde als Feld vorgelesen; Element und Attribute bleiben wie beim rohen Markup.
 */
export function InputOptik({ className = "", ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`input${className ? ` ${className}` : ""}`} {...rest} />;
}

/**
 * Input: reiner Wrapper um die bestehende `.input`-Klasse (docs/KONZEPT-DESIGN-KOMPONENTEN.md
 * § P0), analog zu `Button` oben — kein neues visuelles Design, keine Label/Fehlertext-Logik
 * (das waere ein eigenes `FormField`-Konzept, laut Konzept bewusst nicht Teil dieses Bausteins,
 * weil im Bestand kein klar wiederholtes Muster dafuer auffindbar war). Reicht alle nativen
 * `<input>`-Attribute unveraendert durch (`name`, `type`, `value`/`defaultValue`, `onChange`,
 * `disabled`, `placeholder`, ...) — bestehende Aufrufer bleiben unangetastet (Migration ist P2).
 */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  /*
   * Punkt 82 (22.09.2026): zusaetzlich eine durchgereichte Referenz. Die Platzhalter-Leiste der
   * Mailtexte fuegt an der CURSOR-POSITION der Betreffzeile ein und braucht dafuer Zugriff auf
   * das Feld (`selectionStart`/`setSelectionRange`).
   *
   * Der erste Anlauf umging das mit einem rohen `<input class="input">` — und die Ratsche
   * `design-komponenten-bestand` hat es gefangen (89 → 90). Sie hat recht: ihr Ziel ist, rohes
   * Markup abzubauen, nicht es zu dulden. Eine Referenz hier durchzureichen ist die kleinere und
   * die richtige Aenderung; alle bestehenden Aufrufer bleiben unberuehrt, weil `ref` optional ist.
   */
  function Input({ className = "", ...rest }, ref) {
    return <input ref={ref} className={`input${className ? ` ${className}` : ""}`} {...rest} />;
  },
);

/**
 * Textarea: dasselbe fuer mehrzeilige Felder (Punkt 101 Stufe 2, Mailtext der Saison-Kampagne) —
 * die `.input`-Klasse mit durchgereichter Referenz (Platzhalter an der Schreibstelle einsetzen).
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className = "", ...rest },
  ref,
) {
  return <textarea ref={ref} className={`input${className ? ` ${className}` : ""}`} {...rest} />;
});

/**
 * Checkbox / Radio (Design-Durchsicht E6, 26.09.2026): echte native `<input>`-Elemente mit der
 * MAESTRO-Optik aus `index.css` (`.checkbox`/`.radio`) statt der Browser-Kaestchen. Bewusst KEIN
 * nachgebautes Radix-Konstrukt: Label-Klick, Leertaste, Pfeiltasten in Radiogruppen, FormData und
 * `getByRole("checkbox")` funktionieren unveraendert. `className` haengt an (z. B. `!h-6 !w-6` fuer
 * eine grosse Tippflaeche auf der Gastseite).
 */
export const Checkbox = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type">>(function Checkbox(
  { className = "", ...rest },
  ref,
) {
  return <input ref={ref} type="checkbox" className={`checkbox${className ? ` ${className}` : ""}`} {...rest} />;
});

export const Radio = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type">>(function Radio(
  { className = "", ...rest },
  ref,
) {
  return <input ref={ref} type="radio" className={`radio${className ? ` ${className}` : ""}`} {...rest} />;
});

/** "2026-12-24" (auch mit Uhrzeit-Rest) -> "24.12.2026"; alles andere -> "". */
export function datumIsoZuDe(iso: string | null | undefined): string {
  const m = (iso ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

/** "24.12.2026" / "1.2.2026" / "1.2.26" -> ISO "2026-12-24"; ungueltig oder unvollstaendig -> "". */
export function datumDeZuIso(text: string): string {
  const m = text.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/);
  if (!m) return "";
  const t = Number(m[1]);
  const mo = Number(m[2]);
  const jj = m[3] ?? "";
  const j = jj.length === 2 ? 2000 + Number(jj) : Number(jj);
  const d = new Date(Date.UTC(j, mo - 1, t));
  if (d.getUTCFullYear() !== j || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== t) return "";
  return `${j}-${String(mo).padStart(2, "0")}-${String(t).padStart(2, "0")}`;
}

/**
 * Beim Verlassen des Feldes: ergaenzt ein fehlendes Jahr. „24.10“, „24.10.“ und „2410“ werden zum
 * naechsten Vorkommen dieses Tages ab `heute` (heute selbst zaehlt, sonst naechstes Jahr); alles
 * Uebrige geht durch `datumDeZuIso` („241026“ wird vom Tippen schon zu „24.10.26“). Ungueltig -> "".
 */
export function datumErgaenzen(text: string, heute: Date = new Date()): string {
  const t = text.trim();
  const voll = datumDeZuIso(t);
  if (voll) return voll;
  const m = t.match(/^(\d{1,2})\.(\d{1,2})\.?$/) ?? t.match(/^(\d{2})(\d{2})$/);
  if (!m) return "";
  const heuteIso = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, "0")}-${String(heute.getDate()).padStart(2, "0")}`;
  for (let j = heute.getFullYear(); j <= heute.getFullYear() + 8; j++) {
    const iso = datumDeZuIso(`${m[1]}.${m[2]}.${j}`);
    if (iso && iso >= heuteIso) return iso;
  }
  return "";
}

/** Waehrend des Tippens: nur Ziffern -> Punkte automatisch setzen; wer selbst Punkte tippt, behaelt sie. */
function datumTippen(roh: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(roh)) return datumIsoZuDe(roh); // eingefuegtes ISO-Datum
  if (roh.includes(".")) return roh.replace(/[^0-9.]/g, "").slice(0, 10);
  const z = roh.replace(/\D/g, "").slice(0, 8);
  return z.length > 4 ? `${z.slice(0, 2)}.${z.slice(2, 4)}.${z.slice(4)}` : z.length > 2 ? `${z.slice(0, 2)}.${z.slice(2)}` : z;
}

/**
 * DateInput (Design-Durchsicht E6): Datum IMMER deutsch als TT.MM.JJJJ, unabhaengig von der
 * Browsersprache — das native `type="date"` zeigt je nach Browser „mm/dd/yyyy“ (Befund im
 * englischen Test-Browser), und kein Attribut erzwingt das Format zuverlaessig (dieselbe Lage wie
 * bei `ZeitEingabe`). Nach aussen bleibt alles ISO (`YYYY-MM-DD`): `value`/`defaultValue` rein,
 * `onValueChange` (bei jedem vollstaendigen gueltigen Datum oder Leeren) und `onCommit` (beim
 * Verlassen, Ersatz fuer das bisherige `onBlur`) raus, `name` traegt ein verstecktes Feld mit dem
 * ISO-Wert fuer FormData. Der Kalender-Knopf oeffnet den Browser-Kalender eines unsichtbaren
 * nativen Felds. `className` gilt dem Rahmen (Breite/Layout), `inputClassName` dem Feld.
 */
export function DateInput({
  value,
  defaultValue,
  onValueChange,
  onCommit,
  name,
  min,
  max,
  className = "",
  inputClassName = "",
  ...rest
}: {
  value?: string;
  defaultValue?: string;
  onValueChange?: (iso: string) => void;
  onCommit?: (iso: string) => void;
  name?: string;
  min?: string;
  max?: string;
  className?: string;
  inputClassName?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "defaultValue" | "onChange" | "name" | "min" | "max" | "className">) {
  const [text, setText] = useState(datumIsoZuDe(value ?? defaultValue));
  const [iso, setIso] = useState((value ?? defaultValue ?? "").slice(0, 10));
  const [fehler, setFehler] = useState(false);
  // Touch-Geraete: ein ECHTES natives Datumsfeld ueber dem Symbol (showPicker auf einem
  // unsichtbaren Hilfsfeld bleibt auf iOS oft wirkungslos).
  const [beruehrung] = useState(() => {
    try {
      return typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
    } catch {
      return false;
    }
  });
  const uebergeben = useRef((value ?? defaultValue ?? "").slice(0, 10));
  const knopf = useRef<HTMLSpanElement>(null);
  const kalender = useRef<HTMLInputElement | null>(null);
  const aktuell = useRef({ onValueChange, onCommit });
  aktuell.current = { onValueChange, onCommit };

  // Kontrolliert: ein von aussen gesetzter Wert (z. B. Zuruecksetzen) landet im Feld.
  useEffect(() => {
    if (value === undefined) return;
    const v = value.slice(0, 10);
    setIso(v);
    setText(datumIsoZuDe(v));
    setFehler(false);
    uebergeben.current = v;
  }, [value]);

  useEffect(() => () => kalender.current?.remove(), []);

  function uebernimm(next: string) {
    setIso(next);
    aktuell.current.onValueChange?.(next);
  }

  /** Datum aus dem Kalender (Hilfsfeld oder Touch-Feld): derselbe Weg wie eine getippte Eingabe. */
  function kalenderWahl(v: string) {
    setText(datumIsoZuDe(v));
    setFehler(false);
    uebernimm(v);
    uebergeben.current = v;
    aktuell.current.onCommit?.(v);
  }

  /** Der Browser-Kalender eines unsichtbaren nativen Felds — erst beim Klick und ausserhalb des
   *  Labels angelegt, damit das umgebende `<label>` genau EIN Feld beschriftet (das Textfeld). */
  function kalenderOeffnen() {
    let el = kalender.current;
    if (!el) {
      el = document.createElement("input");
      el.type = "date";
      el.tabIndex = -1;
      el.setAttribute("aria-hidden", "true");
      el.style.cssText = "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;";
      el.addEventListener("change", (e) => {
        const v = (e.target as HTMLInputElement).value;
        kalenderWahl(v);
      });
      document.body.appendChild(el);
      kalender.current = el;
    }
    const r = knopf.current?.getBoundingClientRect();
    if (r) Object.assign(el.style, { left: `${r.left}px`, top: `${r.bottom}px` });
    el.value = iso;
    el.min = min ?? "";
    el.max = max ?? "";
    el.showPicker?.();
  }

  return (
    <span className={`relative inline-flex w-full flex-wrap items-center ${className}`}>
      <input
        {...rest}
        type="text"
        inputMode="decimal"
        aria-invalid={fehler || undefined}
        autoComplete="off"
        placeholder={rest.placeholder ?? "TT.MM.JJJJ"}
        size={11}
        value={text}
        onChange={(e) => {
          const t = datumTippen(e.target.value);
          setText(t);
          setFehler(false);
          const neu = t === "" ? "" : datumDeZuIso(t);
          // V-1304 (02.11.2026): waehrend des Tippens nur ein VIERSTELLIGES Jahr melden. Vorher
          // ging „04.12.20“ (das Zwischenstadium auf dem Weg zu „04.12.2026“) schon als 2020-12-04
          // nach aussen; endete die Eingabe danach unvollstaendig („04.12.202“), blieb 2020 im
          // Formular stehen und landete im Vorgang. Zweistellige Jahre ergaenzt erst der Blur.
          if (t === "" || (neu && /\.\d{4}$/.test(t))) uebernimm(neu);
        }}
        onBlur={(e) => {
          // Fehlendes Jahr wird ergaenzt („24.10“ -> naechstes 24.10.). Unsinn springt NICHT still
          // zurueck, sondern bleibt stehen und bekommt einen Hinweis; gemeldet wird dann nichts.
          const neu = text === "" ? "" : datumErgaenzen(text);
          if (text !== "" && !neu) {
            setFehler(true);
          } else {
            setFehler(false);
            setText(datumIsoZuDe(neu));
            if (neu !== iso) uebernimm(neu);
            // Nur melden, wenn sich etwas geaendert hat (Blur beim Tippen aufs Kalender-Symbol).
            if (neu !== uebergeben.current) {
              uebergeben.current = neu;
              onCommit?.(neu);
            }
          }
          rest.onBlur?.(e);
        }}
        className={`input pr-9 ${inputClassName}`}
      />
      {/* Bewusst kein <button>: das umgebende <label> soll nur das Textfeld beschriften. Der
          Kalender ist eine Maus-Abkuerzung; per Tastatur tippt man das Datum. */}
      <span
        ref={knopf}
        role="button"
        tabIndex={-1}
        aria-label="Kalender öffnen"
        aria-disabled={rest.disabled || undefined}
        onClick={(e) => {
          e.preventDefault();
          if (!rest.disabled && !beruehrung) kalenderOeffnen();
        }}
        className={`absolute right-1.5 grid h-7 w-7 cursor-pointer place-items-center rounded-md text-secondary hover:bg-surface-container-high dark:text-secondary-fixed-dim dark:hover:bg-white/[0.08] ${rest.disabled ? "pointer-events-none opacity-50" : ""}`}
      >
        {/* SVG statt Icon-Schrift: deren Ligatur-Text („calendar_today“) stuende sonst im Label-Text. */}
        <svg aria-hidden="true" viewBox="0 -960 960 960" className="h-4 w-4 fill-current">
          <path d="M200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Zm0-480h560v-80H200v80Zm0 0v-80 80Z" />
        </svg>
        {beruehrung && !rest.disabled && (
          <input
            type="date"
            tabIndex={-1}
            aria-hidden="true"
            value={iso}
            min={min}
            max={max}
            onChange={(e) => kalenderWahl(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        )}
      </span>
      {fehler && (
        <span role="alert" className="mt-1 basis-full text-xs text-red-600 dark:text-red-400">
          Bitte als TT.MM.JJJJ eingeben
        </span>
      )}
      {name && <input type="hidden" name={name} value={iso} />}
    </span>
  );
}

/**
 * Card: reiner Wrapper um die bestehende `.card`-Klasse (`index.css` § "Wiederverwendbare
 * Bausteine im Stitch-Idiom") — analog zu `Button`/`Input` oben (docs/KONZEPT-DESIGN-KOMPONENTEN.md
 * § P0). Kein neues visuelles Design, reicht alle nativen `<div>`-Attribute unveraendert durch
 * (`id`, `onClick`, ...) — bestehende `className="card"`-Aufrufer bleiben unangetastet
 * (Migration ist P2).
 */
export function Card<T extends ElementType = "div">({
  as,
  className = "",
  ...rest
}: { as?: T; className?: string } & Omit<ComponentPropsWithoutRef<T>, "className">) {
  const Tag: ElementType = as ?? "div";
  return <Tag className={`card${className ? ` ${className}` : ""}`} {...rest} />;
}

/**
 * `ZEILEN_AKTION` — die Klassen, die eine SEKUNDAERAKTION innerhalb einer Zeile mit `ZeilenLink`
 * (s. u.) braucht, damit sie ueber der Link-Flaeche liegt und weiter klickbar bleibt: der
 * Overlay-Link liegt auf `z-[1]`, jede Sekundaeraktion muss positioniert (`relative`) und darueber
 * (`z-[2]`) sein. Ausserhalb einer `ZeilenLink`-Zeile sind beide Klassen wirkungslos — ein Element
 * mit `position: relative` ohne Versatz sitzt exakt dort, wo es auch statisch saesse.
 *
 * Bewusst eine Konstante und keine Komponente: die Sekundaeraktionen im Bestand sind sehr
 * verschieden (`<button>`, `<Button>`, Checkbox, ganzes Menue-Wrapper-`<div>`) — sie alle in einen
 * gemeinsamen Wrapper zu zwingen haette Layout (Flex-Kind-Verhalten, `ml-auto`, `shrink-0`)
 * veraendert. Eine Klasse laesst sich an genau das bestehende Element anhaengen.
 *
 * **Ausnahme:** ist das Element bereits `absolute`/`fixed` positioniert (z. B. das „⋯"-Menue der
 * Kanban-Karte), dann NUR `z-[2]` anhaengen, nicht diese Konstante — Tailwind entscheidet bei
 * konkurrierenden Utilities nach der Reihenfolge IM STYLESHEET, nicht im Attribut, und `.relative`
 * steht dort nach `.absolute`. `ZEILEN_AKTION` wuerde die Positionierung also stillschweigend
 * kaputtmachen.
 */
export const ZEILEN_AKTION = "relative z-[2]";

/**
 * `ZeilenLink` — „die ganze Zeile/Karte ist ein echter Link" (Einheit E,
 * `docs/PLAN-VORGANGSSEITE-UMBAU.md`). Antoine, 13.09.2026: „Wenn ich, bevor ich auf einen Link
 * klicke, auf dem Mac Command drücke, öffnet sich ein neuer Tab mit dem Link. Das funktioniert in
 * Maestro nicht. Solche Sachen müssen unbedingt funktionieren. Mac wie Windows."
 *
 * ── Warum ein OVERLAY-Link und nicht ein `<a>` um die Zeile ──────────────────────────────────
 * Die Listenzeilen im Bestand enthalten Sekundaeraktionen: den „⋯"-Knopf der Kanban-Karte, den
 * Erinnerungs-Knopf in `buchungen.tsx`, den „Angebot überprüfen"-Knopf in `AnfragenGruppen.tsx`.
 * Ein `<button>` INNERHALB eines `<a>` ist ungueltiges HTML (beide sind interaktiver Inhalt), und
 * in einer `<table>` kaeme erschwerend dazu, dass zwischen `<tr>` und `<td>` ueberhaupt kein `<a>`
 * stehen darf. Deshalb EIN Kind-Element, absolut positioniert, das die Flaeche der Zeile abdeckt —
 * das etablierte „stretched link"-Muster.
 *
 * ── Was die Zeile dafuer mitbringen muss ─────────────────────────────────────────────────────
 *  1. `relative` auf dem Zeilen-/Karten-Container (bei Tabellen auf dem `<tr>`, nicht auf dem
 *     `<td>` — sonst deckt der Link nur die erste Zelle ab). Relative Positionierung von `<tr>`
 *     ist in CSS Position 3 definiert und in allen Zielbrowsern umgesetzt.
 *  2. Kein `onClick`/`role="button"`/`tabIndex` mehr auf dem Container: der Link ist die eine
 *     Navigation und der eine Tab-Stopp der Zeile. Genau deshalb landet der Tastaturfokus hier
 *     genau EINMAL und nicht zweimal (Container + Link).
 *  3. Jede Sekundaeraktion bekommt `ZEILEN_AKTION` (s. o.).
 * Der Hover-Zustand der Zeile bleibt unveraendert erhalten: der Link ist ein KIND der Zeile, ein
 * Zeiger ueber ihm ist auch ein Zeiger ueber der Zeile (`hover:`/`group-hover:` greifen weiter).
 *
 * ── Was dadurch funktioniert ─────────────────────────────────────────────────────────────────
 * Cmd-/Strg-Klick, Mittelklick, Rechtsklick → „Link in neuem Tab öffnen", Ziel-URL in der
 * Statuszeile, Lesezeichen, Adresse kopieren, Screenreader-Linkliste, Tab/Enter. `<Link>` aus
 * react-router unterbindet die Standardaktion des Browsers NUR beim unmodifizierten Linksklick —
 * jeder modifizierte Klick geht unveraendert an den Browser, deshalb ist hier nichts nachzubauen.
 *
 * `label` ist der zugaengliche Name (der Link hat keinen sichtbaren Text, er liegt ja ueber dem
 * Zeileninhalt) — ein fuer sich verstaendlicher Satz wie „Vorgang von Julia Reimann öffnen", nicht
 * „Öffnen": eine Linkliste aus lauter „Öffnen" ist unbrauchbar.
 *
 * Der Fokusring: `index.css` gibt `a:focus-visible` bereits einen Ring mit `outline-offset: 0` —
 * bei einem Element, das exakt die Zeilenkante abdeckt, laege der genau auf der Kante und
 * verschwaende unter einem `overflow-hidden`-Kartenrahmen. Deshalb hier ein nach INNEN versetzter,
 * voll deckender Ring.
 */
export function ZeilenLink({
  to,
  label,
  className = "",
}: {
  /** Ziel-Adresse der Zeile — dieselbe, die ein Klick vorher per `navigate()` angesteuert hat. */
  to: string;
  /** Zugaenglicher Name des Links (der Link selbst traegt keinen sichtbaren Text). */
  label: string;
  className?: string;
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      className={`absolute inset-0 z-[1] rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:[outline-offset:-2px]${
        className ? ` ${className}` : ""
      }`}
    />
  );
}

/**
 * `istBrowserEigenerKlick` — true, wenn der Browser diesen Klick SELBST erledigt (neuer Tab per
 * Cmd/Strg, neues Fenster per Shift, Speichern-unter per Alt, Mittelklick) und die Anwendung
 * deshalb keinen eigenen Seiteneffekt ausloesen darf.
 *
 * Gebraucht ueberall dort, wo an einem `<Link>` ZUSAETZLICH etwas passieren soll (z. B. „Palette
 * schliessen" in `ui/GlobalSearch.tsx`): `<Link>` selbst laesst den modifizierten Klick korrekt
 * durch, ein eigenes `onClick` liefe aber trotzdem — und haette die Oberflaeche unter dem gerade
 * im Hintergrund geoeffneten Tab weggeraeumt.
 */
export function istBrowserEigenerKlick(e: {
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  button: number;
}): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

/**
 * CardRow: Kopfzeile innerhalb einer `Card` — Titel plus optionale rechtsbuendige Aktionen, mit
 * Trennlinie nach unten. Ergaenzt (docs/KONZEPT-DESIGN-KOMPONENTEN.md § P0-Vorgabe), weil sich
 * beim Bau von `Card` ein klar wiederholtes Muster zeigte: derselbe `<h3>`-Titel direkt nach dem
 * oeffnenden `.card`-Div, mit identischer Rand-/Abstands-Klasse, an FUENF Stellen in
 * `pages/customer-detail.tsx` — Zeilen 529-533 "Kontakt", 630-634 "Rechnungen", 667-671
 * "Notizen", 710-713 "Historie" (mit zusaetzlichen Filter-Aktionen), 1858-1863
 * "Gegenprüfung"/Gegenüberstellung. `actions` bildet die dort an einer Stelle vorkommende
 * `ml-auto flex flex-wrap gap-1.5`-Aktionsleiste ab (z. B. die Filter-Buttons bei "Historie").
 * Migration dieser Fundstellen selbst ist NICHT Teil dieses Kriteriums (P2).
 */
export function CardRow({
  title,
  actions,
  className = "",
}: {
  title: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-wrap items-center gap-3.5 border-b border-outline-variant/30 px-[18px] py-[14px] dark:border-outline/30${className ? ` ${className}` : ""}`}
    >
      <h3 className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">{title}</h3>
      {actions != null && <div className="ml-auto flex flex-wrap gap-1.5">{actions}</div>}
    </div>
  );
}

/**
 * `WertAenderung` — alter Wert durchgestrichen, Pfeil, neuer Wert, optional ein Akteur-/Zeit-
 * Untertext. Erster Diff-Baustein im Design-System (PR4, `docs/PLAN-EVENT-FELD-VERSIONIERUNG.md`
 * Abschnitt 4.5 "erster Diff-Baustein im Repo, gehoert ins DS (drei Aufrufer)"): am Feld selbst
 * in `ui/auftrag/AuftragEckdatenKarte.tsx` (nur Aenderungen NACH Zusage, ohne `akteur`/`zeitpunkt` —
 * das steht bereits im Banner darueber), je Zeile in `ui/auftrag/FeldVersionenDrawer.tsx` (MIT
 * `akteur`/`zeitpunkt`) und im gebuendelten Restore-Toast-Kontext.
 *
 * Bewusst `<span>`-basiert und inline (kein `<div>`): muss sowohl neben einem Feldlabel als auch
 * innerhalb einer Drawer-Zeile funktionieren, ohne dort das Flex-/Grid-Layout des Aufrufers zu
 * stoeren.
 */
export function WertAenderung({
  alt,
  neu,
  akteur,
  zeitpunkt,
  className = "",
}: {
  /** Der Wert VOR der Aenderung — bereits fertig formatiert (z. B. `formatiereFeldWert`). */
  alt: string;
  /** Der Wert NACH der Aenderung — bereits fertig formatiert. */
  neu: string;
  /** Optional: wer hat geaendert (Name/Label). Ohne `zeitpunkt` sinnlos, beide sind daher an das
   *  gemeinsame Untertext-Paar gekoppelt statt zwei unabhaengige Props zu sein. */
  akteur?: string | null;
  /** Optional: wann, bereits fertig formatiert (z. B. "heute, 22:04 Uhr"). */
  zeitpunkt?: string | null;
  className?: string;
}) {
  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 ${className}`}>
      <span className="whitespace-nowrap font-body-sm text-[13px] font-medium text-secondary line-through dark:text-secondary-fixed-dim">
        {alt}
      </span>
      <Icon name="arrow_forward" className="text-[12px] text-secondary-fixed-dim" />
      <span className="whitespace-nowrap font-body-md text-[14px] font-semibold text-on-background dark:text-inverse-on-surface">
        {neu}
      </span>
      {(akteur || zeitpunkt) && (
        <span className="basis-full font-body-sm text-[11px] text-secondary-fixed-dim">
          {[akteur, zeitpunkt].filter(Boolean).join(" · ")}
        </span>
      )}
    </span>
  );
}

/**
 * Logo: M-Kachel (+ optionale Wortmarke) — Design-Komponenten-Konzept P0
 * (docs/KONZEPT-DESIGN-KOMPONENTEN.md § "Inline-Muster ohne klaren Extraktions-Nutzen"):
 * mindestens 10 unabhaengige Fundstellen mit "identischer Absicht", aber leicht abweichenden
 * Massen/Radien/Schriftgroessen (AppShell.tsx, AdminShell.tsx, login.tsx, chat-public.tsx,
 * CopilotPanel.tsx, offer-public.tsx, mein-konto.tsx). Zwei Erscheinungsformen teilen sich
 * dieselbe Kachel-Form:
 *  - `variant="solid"` (Standard): volle `bg-primary`-Flaeche + `text-on-primary` — die eigentliche
 *    Marke, im Bestand immer mit dem Buchstaben "M" (Default fuer `buchstabe`).
 *  - `variant="soft"`: `bg-primary/15` + `text-primary` — dieselbe Kachel-Form, aber als
 *    Initialen-Avatar (Betriebs-/Personen-Initiale statt "M") verwendet: Betriebs-Umschalter und
 *    Konto-Menue in `AppShell.tsx`, Profilbild-Platzhalter in `mein-konto.tsx`. Gleiche visuelle
 *    Absicht wie die Marken-Kachel (Konzept: "identischer Absicht"), deshalb hier vereinheitlicht
 *    statt einer zweiten Komponente.
 * Die Bestandswerte streuen NICHT nach einem festen sm/md/lg-Raster (sieben verschiedene
 * Kantenlaengen zwischen 26 und 64px, vier Radien, unterschiedliche Schriftgroessen je Stelle) —
 * deshalb keine erfundene Groessen-Aufzaehlung, sondern die tatsaechlich beobachteten Werte als
 * Props (`size` in px wie bei `Modal`s `style={{ width }}` oben, `radiusClassName`,
 * `textClassName` fuer Schriftgroesse + `leading-none`, wo im Original vorhanden). `font-serif
 * font-bold` ist an JEDER Fundstelle identisch und deshalb fest verdrahtet.
 * `children` haengt zusaetzlichen Inhalt INNERHALB der Kachel an (das KI-Abzeichen in
 * `CopilotPanel.tsx`) — dafuer erlaubt `className` das dort noetige `relative` auf der Kachel
 * selbst, ohne dass die Komponente das Abzeichen kennen muesste (reine Praesentation).
 * Die Wortmarke ("MAESTRO"/"MAESTRO Admin") ist an jeder der drei Fundstellen, die sie zeigen,
 * unterschiedlich gestylt (Farbe/Tracking/Schriftgroesse je nach hellem/dunklem Hintergrund) —
 * `wortmarkeClassName` reicht deshalb die exakte Bestand-Klasse durch, statt einen erfundenen
 * gemeinsamen Standard vorzugeben.
 */
export function Logo({
  size = 26,
  radiusClassName = "rounded-lg",
  variant = "solid",
  textClassName = "text-[14px] leading-none",
  buchstabe = "M",
  mitWortmarke = false,
  wortmarke = "MAESTRO",
  wortmarkeClassName = "",
  className = "",
  ariaHidden = false,
  children,
}: {
  /** Kantenlaenge der quadratischen Kachel in px (width = height = size). */
  size?: number;
  radiusClassName?: string;
  variant?: "solid" | "soft";
  textClassName?: string;
  buchstabe?: string;
  mitWortmarke?: boolean;
  wortmarke?: ReactNode;
  wortmarkeClassName?: string;
  className?: string;
  /** Bestand-Muster `CopilotPanel.tsx` (Randleiste, eingeklappter Zustand): die ganze Kachel
   *  sitzt in einem eigens `aria-label`ten `<button>` und ist rein dekorativ — `aria-hidden`
   *  vermeidet doppelte/verwirrende Vorlesung durch Screenreader. */
  ariaHidden?: boolean;
  /** Zusaetzlicher Inhalt INNERHALB der Kachel, z. B. das absolut positionierte KI-Abzeichen in
   *  `CopilotPanel.tsx` (dafuer traegt `className` dort zusaetzlich `relative`). */
  children?: ReactNode;
}) {
  const farbKlasse = variant === "solid" ? "bg-primary text-on-primary" : "bg-primary/15 text-primary";
  return (
    <>
      <div
        className={`grid shrink-0 place-items-center ${radiusClassName} ${farbKlasse}${className ? ` ${className}` : ""}`}
        style={{ width: size, height: size }}
        aria-hidden={ariaHidden ? "true" : undefined}
      >
        <span className={`font-serif font-bold ${textClassName}`}>{buchstabe}</span>
        {children}
      </div>
      {mitWortmarke && <span className={wortmarkeClassName}>{wortmarke}</span>}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Admin-Bereich V2 (docs/KONZEPT-ADMIN-BEREICH.md § "Architektur-Entscheidung" Punkt 4 +
 * § "Informationsarchitektur & UI"): vier neue Bausteine auf Radix-Unterbau, gestylt mit den
 * Bestand-Tokens von oben (`.tab-scroll`, `.input`, `.btn-*`, `PILL_CLASSES`). Radix liefert nur
 * Verhalten (Fokus-Trap, Tastatur, ARIA, Portale) — das Aussehen bleibt 1:1 der Bestand, nichts
 * Bestehendes wird in dieser Iteration umgestellt (weder `tenant-detail.tsx` noch ein natives
 * `<select>`/`window.alert`-Aufruf irgendwo im Repo).
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * TabBar: duenner Wrapper um `@radix-ui/react-tabs`, gestylt exakt im bestehenden
 * `.tab-scroll`-Muster (visuelles Vorbild `settings.tsx`: Buttons mit `-mb-px border-b-2` fuer den
 * aktiven Tab). Radix ersetzt hier nur den lokalen `tab === t.key`-Vergleich durch
 * `data-[state=active]` und ergaenzt Tastatur-Navigation (Pfeiltasten zwischen Tabs) + korrekte
 * ARIA-Rollen (`tablist`/`tab`), die die bisherigen reinen `<button>`-Leisten (z. B. `settings.tsx`,
 * `AdminShell.tsx`) nicht hatten.
 *
 * `children` ist optional: gesetzt, wird er als EIN `Tabs.Content` fuer den aktuell aktiven Wert
 * gerendert — der uebliche Fall in diesem Repo bleibt aber, dass der Aufrufer den Panel-Inhalt
 * selbst per `{tab === "x" && (...)}` unterhalb schaltet (wie `settings.tsx` es bereits tut) und
 * `TabBar` nur die Kopfzeile stellt; dann bleibt `children` einfach weg.
 */
export function TabBar({
  tabs,
  value,
  onValueChange,
  children,
}: {
  tabs: { value: string; label: string }[];
  value: string;
  onValueChange: (value: string) => void;
  children?: ReactNode;
}) {
  const { ref, rand } = useReiterScroll<HTMLDivElement>(value);
  return (
    <Tabs.Root value={value} onValueChange={onValueChange}>
      <div className="relative">
        <Tabs.List
          ref={ref}
          data-rand={rand || undefined}
          className="tab-scroll flex gap-8 border-b border-outline-variant/30 dark:border-outline/30"
          aria-label="Tabs"
        >
          {tabs.map((t) => (
            <Tabs.Trigger
              key={t.value}
              value={t.value}
              className="-mb-px whitespace-nowrap border-b-2 border-transparent pb-4 font-label-md text-[12.5px] font-semibold text-on-surface-variant transition-colors hover:text-primary data-[state=active]:border-primary data-[state=active]:text-primary dark:text-secondary-fixed-dim dark:hover:text-primary-fixed dark:data-[state=active]:border-primary-fixed dark:data-[state=active]:text-primary-fixed"
            >
              {t.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <ReiterRandPfeile rand={rand} />
      </div>
      {children != null && <Tabs.Content value={value}>{children}</Tabs.Content>}
    </Tabs.Root>
  );
}

/**
 * ConfirmSlugModal: verallgemeinert das bestehende Loesch-Bestaetigungs-Muster aus
 * `tenant-detail.tsx` (Zeile ~466-501, Stand vor dieser Aenderung) — Slug/Name muss EXAKT
 * eingetippt werden, der Bestaetigen-Knopf bleibt bis dahin `disabled`. Wrapper um
 * `@radix-ui/react-dialog` (kein eigenes AlertDialog-Paket noetig, `react-dialog` deckt
 * Fokus-Trap/Escape/Portal/ARIA bereits ab — Konzept nennt "AlertDialog-artig, aber react-dialog
 * reicht").
 *
 * `onConfirm` darf ein Promise zurueckgeben (Regelfall: eine API-Mutation). Wirft/lehnt es ab,
 * zeigt der Dialog die Fehlermeldung inline und bleibt offen (identisch zum bisherigen
 * `deleteError`-Verhalten im Bestand); loest es erfolgreich auf, schliesst der Dialog NICHT von
 * selbst — das entscheidet der Aufrufer ueber `onClose`/eigenen State (z. B. weil er nach Erfolg
 * zusaetzlich navigieren will, wie `onBack()` es heute in `tenant-detail.tsx` tut). Waehrend
 * `busy` ist Schliessen (Escape/Klick ausserhalb/Abbrechen) gesperrt, wie `deleteBusy` es im
 * Bestand tut.
 *
 * `gefahrTon` (Vorgabe `false`) steuert nur die Knopf-Farbe (rot statt Primaerfarbe) fuer
 * tatsaechlich destruktive Aktionen (Sicherheitsklasse D, z. B. Loeschen) — Klasse-C-Aktionen mit
 * getippter Bestaetigung (z. B. Archivieren) bleiben ohne roten Knopf.
 *
 * In DIESER Iteration wird NUR der Baustein gebaut — `tenant-detail.tsx` bleibt unveraendert
 * (separates, im Konzept explizit genanntes Folgekriterium).
 */
export function ConfirmSlugModal({
  open,
  onClose,
  onConfirm,
  titel,
  warnungstext,
  erwarteterText,
  bestaetigungslabel,
  gefahrTon = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (eingabe: string) => void | Promise<void>;
  titel: string;
  warnungstext: ReactNode;
  erwarteterText: string;
  bestaetigungslabel: string;
  gefahrTon?: boolean;
}) {
  const [eingabe, setEingabe] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bei jedem Oeffnen zuruecksetzen - sonst zeigt ein zweiter Aufruf noch die Eingabe/den Fehler
  // des vorherigen (gleiches Muster wie `openDelete()` im Bestand).
  useEffect(() => {
    if (open) {
      setEingabe("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  function requestClose() {
    if (busy) return;
    onClose();
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (eingabe !== erwarteterText || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(eingabe);
      setBusy(false);
    } catch (err) {
      // Kein stiller Fehlschlag, keine falsche Erfolgsmeldung: der Dialog bleibt offen, die
      // Ursache steht direkt darin (Muster `deleteError` in `tenant-detail.tsx`).
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && requestClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/45" />
        <Dialog.Content
          onEscapeKeyDown={(e) => busy && e.preventDefault()}
          onPointerDownOutside={(e) => busy && e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-[60] w-[calc(100vw-3rem)] max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface-container-lowest p-6 shadow-2xl dark:bg-night-card"
        >
          <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
            <Dialog.Title
              className={`font-body-md text-body-md font-bold ${gefahrTon ? "text-error" : "text-on-background dark:text-inverse-on-surface"}`}
            >
              {titel}
            </Dialog.Title>
            <Dialog.Description className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              {warnungstext}
            </Dialog.Description>
            <label className="block">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Zur Bestätigung: „{erwarteterText}" exakt eintippen
              </span>
              <Input
                type="text"
                required
                autoComplete="off"
                className="font-mono"
                value={eingabe}
                onChange={(e) => setEingabe(e.target.value)}
                placeholder={erwarteterText}
              />
            </label>
            {error && <ErrorNote message={error} />}
            <div className="flex justify-end gap-2.5">
              <Dialog.Close asChild>
                <Button variant="secondary" disabled={busy}>
                  Abbrechen
                </Button>
              </Dialog.Close>
              <Button type="submit" variant={gefahrTon ? "danger" : "primary"} disabled={busy || eingabe !== erwarteterText}>
                {busy ? "Wird ausgeführt …" : bestaetigungslabel}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Select: duenner Wrapper um `@radix-ui/react-select`, gestylt wie `.input` (Bestand-Klasse fuer
 * alle Formularfelder) — optisch nicht von normalen Text-Inputs zu unterscheiden, nur mit einem
 * Chevron statt des nativen Browser-Pfeils. Ersetzt kein bestehendes `<select>` in dieser
 * Iteration (Loop-Regel: keine bestehende Seite anfassen).
 */
export function Select({
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder,
  ariaLabel,
  disabled,
  name,
  required,
  id,
  className = "",
  testId,
}: {
  value?: string;
  /** Unkontrolliert (mit `name` fuer FormData-Formulare): Radix legt dann ein verstecktes natives
   *  Feld an, das beim Absenden mitgeht — E6 ersetzt damit die letzten FormData-`<select>`. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  name?: string;
  required?: boolean;
  id?: string;
  /** Haengt am Ausloeser, z. B. `!w-auto !py-1.5` fuer kompakte Filter. */
  className?: string;
  testId?: string;
}) {
  // Radix verbietet `""` als Item-Wert (reserviert fuer „nichts gewaehlt“). Eine echte leere Option
  // („— Alle —“) laeuft deshalb intern ueber einen Platzhalterwert und kommt nach aussen als `""`.
  const LEER = "__leer__";
  const hatLeer = options.some((o) => o.value === "");
  const rein = (v: string | undefined) => (v === "" && hatLeer ? LEER : v);
  return (
    <RadixSelect.Root
      value={rein(value)}
      defaultValue={rein(defaultValue)}
      // Ein rohes `""` waehlt nie ein Mensch (die leere Option kommt als LEER). Radix meldet es,
      // wenn sich `value` von aussen aendert, bevor die Optionen im versteckten nativen Select
      // stehen (erstes Laden) — durchgereicht loeschte es den gerade geladenen Wert.
      onValueChange={(v) => v !== "" && onValueChange?.(v === LEER ? "" : v)}
      disabled={disabled}
      name={name}
      required={required}
    >
      <RadixSelect.Trigger
        id={id}
        aria-label={ariaLabel}
        data-testid={testId}
        className={`input flex w-full items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50${className ? ` ${className}` : ""}`}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <Icon name="expand_more" className="text-[16px] text-secondary dark:text-secondary-fixed-dim" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          className="z-[70] max-h-[var(--radix-select-content-available-height)] overflow-hidden rounded-lg border border-outline-variant bg-surface-container-lowest shadow-[0_8px_18px_-10px_rgba(45,38,26,0.24)] dark:border-outline/30 dark:bg-night-card"
        >
          <RadixSelect.Viewport className="p-1">
            {options.map((opt) => (
              <RadixSelect.Item
                key={opt.value}
                value={opt.value === "" ? LEER : opt.value}
                className="cursor-pointer select-none rounded-md px-3 py-2 font-body-sm text-body-sm text-on-background outline-none data-[highlighted]:bg-surface-container-high data-[state=checked]:font-semibold dark:text-inverse-on-surface dark:data-[highlighted]:bg-white/[0.08]"
              >
                <RadixSelect.ItemText>{opt.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

/**
 * ChipSelect: eine Auswahl als abgerundeter Filter-Chip mit Icon — die Form, die die
 * Anfragen-Seite seit dem freigegebenen Kanban-Entwurf (08.09.2026) fuer ihre Filter benutzt.
 *
 * WARUM ALS BAUSTEIN UND NICHT WEITER ROH IN DER SEITE: die Anfragen-Seite trug diese Form
 * zweimal fast wortgleich als rohes `<select>` im Markup. Als am 21.09.2026 ein DRITTER Filter
 * dazukam („Lage"), meldete die Design-Ratsche `design-komponenten-bestand.test.ts` sofort einen
 * Zuwachs von 16 auf 17 rohe Stellen — zu Recht: ihr Ziel ist 0. Die Zahl einfach hochzusetzen
 * waere genau die Lockerung, die im Haus verboten ist. Stattdessen zieht dieser Baustein den
 * Bestand NACH UNTEN (16 -> 14): alle drei Chips der Seite laufen jetzt durch ihn.
 *
 * BEWUSST ein natives `<select>` und NICHT die `Select`-Komponente daneben: die traegt die
 * `.input`-Optik (ein voller Kasten mit Rahmen), und drei Filter nebeneinander in zwei
 * verschiedenen Formen waeren schlechter als das, was heute dasteht. `ui/components.tsx` ist vom
 * Zaehler der Ratsche ausdruecklich ausgenommen — genau fuer Bausteine wie diesen.
 *
 * Verhalten und Aussehen sind 1:1 das bisherige Markup: aktiv (`wert !== neutral`) faerbt sich der
 * Chip im Akzentton, sonst bleibt er neutral.
 */
export function ChipSelect({
  wert,
  onWechsel,
  icon,
  ariaLabel,
  optionen,
  neutral = "all",
  testId,
}: {
  wert: string;
  onWechsel: (wert: string) => void;
  icon: string;
  ariaLabel: string;
  optionen: ReadonlyArray<{ wert: string; text: string }>;
  /** Der Wert, bei dem der Chip als „nicht gesetzt" gilt. */
  neutral?: string;
  testId?: string;
}) {
  const aktiv = wert !== neutral;
  return (
    <label
      data-testid={testId}
      /* `rounded-[9px]` ist der Entwurfswert aus 40b (`border-radius:9px`): dort ist KEIN Element
         der Werkzeugleiste eine runde Pille — Suchfeld, Filter und Ansichtsumschalter tragen
         dieselbe weiche Ecke. Bis hierher waren es volle Pillen. */
      className={`relative flex items-center gap-1.5 rounded-[9px] border px-3 py-1.5 pr-7 motion-safe:transition-colors ${
        aktiv
          ? "border-primary/40 bg-primary/10 dark:bg-primary/15"
          : "border-outline-variant bg-surface-container-lowest dark:bg-inverse-surface"
      }`}
    >
      <Icon name={icon} className={`text-[15px] ${aktiv ? "text-primary" : "text-on-surface-variant/70"}`} />
      <select
        aria-label={ariaLabel}
        className={`appearance-none bg-transparent text-[12px] font-medium outline-none ${
          aktiv ? "text-primary" : "text-on-surface-variant dark:text-inverse-on-surface"
        }`}
        value={wert}
        onChange={(e) => onWechsel(e.target.value)}
      >
        {optionen.map((o) => (
          <option key={o.wert} value={o.wert}>
            {o.text}
          </option>
        ))}
      </select>
      <Icon
        name="expand_more"
        className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[16px] ${
          aktiv ? "text-primary" : "text-on-surface-variant/70"
        }`}
      />
    </label>
  );
}

/**
 * Aufklapper: ein kleines Fenster, das an einem Auslöser hängt — das Menü „⋯“ und die Sprach-Pille
 * im Kopf der Vorgangsseite (Variante „Salon“, Antoine 26.09.2026). Bis dahin baute jede Stelle das
 * von Hand (Kanban-Karte, altes „Status ändern“-Menü): absolut positioniert, ohne Schließen bei
 * Klick daneben oder Escape. Hier einmal richtig: schließt bei Klick außerhalb, bei Escape und
 * über `schliessen` aus dem Inhalt heraus.
 *
 * `ausloeser` bekommt den Zustand und rendert den Knopf selbst (Pille, Icon-Knopf …); `children`
 * bekommt `schliessen`. Die Breite begrenzt sich am Handy auf den Bildschirm, damit ein offenes
 * Fenster nie waagerecht scrollen lässt. Ragt das Fenster trotzdem über eine Bildschirmkante
 * (am Handy: rechtsbündig an einer Pille, die links steht — Antoine 27.09.2026, Sprachfenster
 * links abgeschnitten), schiebt es sich waagerecht zurück, bis 16 px Rand zu beiden Seiten bleiben.
 */
export function Aufklapper({
  ausloeser,
  children,
  ausrichtung = "rechts",
  className = "w-64",
  rolle = "menu",
}: {
  ausloeser: (p: { offen: boolean; umschalten: () => void }) => ReactNode;
  children: (schliessen: () => void) => ReactNode;
  /** An welcher Kante des Auslösers das Fenster bündig sitzt. */
  ausrichtung?: "links" | "rechts";
  /** Breite und Innenabstand des Fensters. */
  className?: string;
  /** `menu` für Aktionslisten, `dialog` für ein Fenster mit Feldern (Sprachwahl). */
  rolle?: "menu" | "dialog";
}) {
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);
  const fenster = useRef<HTMLDivElement>(null);
  // Vor dem Zeichnen messen, damit das Fenster nie sichtbar aus dem Bild springt.
  useLayoutEffect(() => {
    const el = fenster.current;
    if (!offen || !el) return;
    el.style.transform = "";
    const { left, right } = el.getBoundingClientRect();
    const rand = 16;
    const rechteGrenze = document.documentElement.clientWidth || window.innerWidth;
    const verschub = left < rand ? rand - left : right > rechteGrenze - rand ? rechteGrenze - rand - right : 0;
    if (verschub) el.style.transform = `translateX(${Math.round(verschub)}px)`;
  }, [offen]);
  useEffect(() => {
    if (!offen) return;
    function klick(e: MouseEvent) {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false);
    }
    function taste(e: KeyboardEvent) {
      if (e.key === "Escape") {
        // Esc schliesst NUR das Fenster, nicht zusaetzlich den Dialog/Drawer darunter
        // (Capture-Phase, damit deren Document-Listener den Tastendruck nicht mehr sehen).
        e.stopPropagation();
        setOffen(false);
      }
    }
    document.addEventListener("mousedown", klick);
    document.addEventListener("keydown", taste, true);
    return () => {
      document.removeEventListener("mousedown", klick);
      document.removeEventListener("keydown", taste, true);
    };
  }, [offen]);
  return (
    <div className="relative" ref={huelle}>
      {ausloeser({ offen, umschalten: () => setOffen((v) => !v) })}
      {offen && (
        <div
          ref={fenster}
          role={rolle}
          className={`absolute top-full z-30 mt-1.5 max-w-[calc(100vw-2rem)] rounded-xl border border-outline-variant/40 bg-surface-container-lowest py-1.5 shadow-2xl dark:border-outline/30 dark:bg-night-card ${
            ausrichtung === "rechts" ? "right-0" : "left-0"
          } ${className}`}
        >
          {children(() => setOffen(false))}
        </div>
      )}
    </div>
  );
}

/** Ein Eintrag in einem `Aufklapper`-Menü: Icon + Beschriftung, optional rot für Absage/Löschen. */
export function MenueEintrag({
  icon,
  ton = "normal",
  children,
  className = "",
  ...rest
}: { icon?: string; ton?: "normal" | "gefahr" } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left font-body-sm text-[13px] hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-surface-variant/20 ${
        ton === "gefahr" ? "text-error" : "text-on-background dark:text-inverse-on-surface"
      } ${className}`}
      {...rest}
    >
      {icon && <Icon name={icon} className={`text-[17px] ${ton === "gefahr" ? "" : "text-on-surface-variant/70"}`} />}
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );
}

/** Zwischenüberschrift in einem `Aufklapper`-Menü (z. B. „Status ändern“). */
export function MenueTitel({ children }: { children: ReactNode }) {
  return (
    <p className="px-3.5 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-secondary-fixed-dim">{children}</p>
  );
}

/** Segment-Umschalter im Stil der Varianten-Kachel (drei bis vier gleich breite Knöpfe). */
export function Segmente<K extends string>({ werte, wert, onWahl, label }: { werte: readonly { key: K; label: string; aus?: boolean }[]; wert: K; onWahl: (k: K) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex gap-1 rounded-[10px] bg-surface-container-high p-1 dark:bg-surface-variant/20">
      {werte.map((w) => (
        <button
          key={w.key}
          type="button"
          role="radio"
          aria-checked={wert === w.key}
          disabled={w.aus}
          onClick={() => onWahl(w.key)}
          className={`flex-1 rounded-[8px] px-3 py-1.5 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            wert === w.key ? "bg-white text-on-background shadow-sm dark:bg-night-card dark:text-inverse-on-surface" : "text-on-surface-variant hover:text-on-background dark:text-secondary-fixed-dim"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Toggle: einfacher Ja/Nein-Schalter (P4, `docs/KONZEPT-ADMIN-BEREICH.md` § "Betriebsschalter ...
 * editieren"). Dieselbe visuelle Sprache wie `ToggleSwitch` in `pages/settings.tsx` (dort
 * modul-intern, nicht exportiert, mit zusaetzlichem `disabledHint`/`children`-Fussnotenbereich,
 * den der Admin-Bereich nicht braucht) — kein neues Design, derselbe `role="switch"`-Knopf mit
 * denselben Tailwind-Klassen, nur schlanker (keine Fussnote, kein separates Deaktivierungs-Label).
 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-primary" : "bg-surface-variant dark:bg-surface-dim"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/**
 * Toast-System: Wrapper um `@radix-ui/react-toast` + `useToast()`-Hook — der geplante Ersatz fuer
 * `window.alert` im Admin-Bereich (Konzept § "Informationsarchitektur & UI"). In DIESER Iteration
 * nur die Infrastruktur: kein bestehender `window.alert`-Aufruf wird ersetzt (separates
 * Folgekriterium).
 *
 * `ToastProvider` muss den Admin-Host EINMAL umschliessen. `useToast()` wirft absichtlich, wenn es
 * ausserhalb aufgerufen wird — ein stiller No-Op waere hier der klassische "sieht aus wie
 * erledigt"-Fehler (CLAUDE.md § "haeufigste Fehlerklasse"): eine vergessene
 * `<ToastProvider>`-Einbindung soll sofort auffallen, nicht als lautlos verschluckter
 * `toast.success(...)`-Aufruf.
 *
 * Farben: dieselben Ton-Paare wie ueberall sonst im Repo (`PILL_CLASSES.success`/`.danger`, oben
 * bereits importiert) — keine neuen Farbwerte.
 */
interface ToastEintrag {
  id: string;
  ton: "success" | "error";
  text: string;
}

const ToastContext = createContext<{ push: (ton: ToastEintrag["ton"], text: string) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [eintraege, setEintraege] = useState<ToastEintrag[]>([]);

  function push(ton: ToastEintrag["ton"], text: string) {
    setEintraege((cur) => [...cur, { id: crypto.randomUUID(), ton, text }]);
  }

  function entferne(id: string) {
    setEintraege((cur) => cur.filter((e) => e.id !== id));
  }

  return (
    <ToastContext.Provider value={{ push }}>
      <Toast.Provider swipeDirection="right" duration={5000}>
        {children}
        {eintraege.map((e) => (
          <Toast.Root
            key={e.id}
            className={`pointer-events-auto flex items-start gap-2.5 rounded-xl px-4 py-3 shadow-lg ${
              e.ton === "success" ? PILL_CLASSES.success : PILL_CLASSES.danger
            }`}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) entferne(e.id);
            }}
          >
            <Icon name={e.ton === "success" ? "check_circle" : "error"} className="mt-0.5 shrink-0 text-[16px]" />
            <Toast.Description className="font-body-sm text-body-sm">{e.text}</Toast.Description>
            <Toast.Close aria-label="Schließen" className="ml-auto shrink-0 opacity-70 hover:opacity-100">
              <Icon name="close" className="text-[14px]" />
            </Toast.Close>
          </Toast.Root>
        ))}
        <Toast.Viewport className="fixed bottom-4 right-4 z-[80] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): { success: (text: string) => void; error: (text: string) => void } {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() muss innerhalb von <ToastProvider> aufgerufen werden.");
  return {
    success: (text: string) => ctx.push("success", text),
    error: (text: string) => ctx.push("error", text),
  };
}

/**
 * Wie `useToast`, wirft aber nicht ohne `<ToastProvider>`: die Meldung entfällt dann still. Für
 * Stellen, an denen die Meldung eine Zugabe ist (P4: „Zahlungslink an … gesendet"), die Aktion
 * selbst aber auch in Seiten und Tests ohne Provider funktionieren muss.
 */
export function useToastWennVorhanden(): { success: (text: string) => void; error: (text: string) => void } {
  const ctx = useContext(ToastContext);
  return {
    success: (text: string) => ctx?.push("success", text),
    error: (text: string) => ctx?.push("error", text),
  };
}
